import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

type View = 'resolve' | 'home' | 'golden' | 'review' | 'governance' | 'playground' | 'audit' | 'settings' | 'notifications';
type WizardStep = 1 | 2 | 3 | 4;
interface Supplier {
  tenant_supplier_id?: string;
  datax_id: string;
  master_legal_name: string;
  master_tax_id?: string;
  master_duns_number?: string;
  master_website?: string;
  logo_url?: string;
  tenant_supplier_code?: string;
  tenant_status?: string;
  tenant_classification?: string;
  data_quality_score?: number;
  city?: string;
  state_province?: string;
  country?: string;
  xref_count?: number;
  last_catalog_sync?: number;
  updated_at?: number;
}

interface SourceRecord {
  source_record_id: string;
  source_name: string;
  source_supplier_name: string;
  document_type?: string;
  document_id?: string;
  resolved_entity?: string;
  gl_code?: string;
  evidence?: string;
  source_tax_id?: string;
  source_duns?: string;
  source_website?: string;
  source_address?: string;
  source_city?: string;
  source_state?: string;
  source_postal_code?: string;
  source_category?: string;
  match_score?: number;
  merge_method?: string;
  match_summary?: string;
  xref_id?: string;
}

interface SupplierProfileData {
  certifications: any[];
  esg_scores: any[];
  environmental: any[];
  risk_assessments: any[];
  diversity: any[];
  compliance: any[];
  sanctions: any[];
  performance: any[];
  documents: any[];
  relationships: any[];
  contacts: any[];
  locations: any[];
}

interface ReviewItem {
  review_id: string;
  source_record_id: string;
  source_supplier_name: string;
  source_name: string;
  match_score: number;
  candidate_datax_id: string;
  candidate_name: string;
  candidate_logo?: string;
  comments?: string;
  match_summary?: string;
  scoring_status?: 'scored' | 'pending_scoring';
  assigned_to_user_id?: string | null;
  assigned_to_name?: string | null;
  assigned_to_email?: string | null;
  created_at?: number;
  updated_at?: number;
  source_tax_id?: string;
  source_duns?: string;
  source_website?: string;
  source_address?: string;
  source_category?: string;
}

interface TenantUser {
  user_id: string;
  full_name: string;
  email?: string;
  role?: string;
}

interface MatchSignal {
  label: string;
  score: number;
  weight: number;
  detail: string;
}

interface Activity {
  source_record_id: string;
  source_supplier_name: string;
  source_name: string;
  record_status: string;
  review_status?: string;
  match_score?: number;
  master_legal_name?: string;
  datax_id?: string;
  merge_method?: string;
  merged_records?: number;
  comments?: string;
  match_summary?: string;
  scoring_status?: 'scored' | 'pending_scoring';
  assigned_to_name?: string;
  assigned_to_user_id?: string;
  updated_at?: number;
  created_at?: number;
  queue_state?: string;
  run_label?: string;
  run_type?: string;
  review_id?: string;
  source_tax_id?: string;
  source_duns?: string;
  source_website?: string;
  source_address?: string;
  source_category?: string;
}
interface GsrSeed {
  source_record_id?: string;
  source_supplier_name?: string;
  candidate_name?: string;
  candidate_datax_id?: string;
  match_score?: number;
}

interface ConnectionSummary {
  source_system_id: string;
  source_name: string;
  connection_type: string;
  trust_score: number;
  status: string;
  total_records: number;
  review_records: number;
  matched_records: number;
  rejected_records: number;
  new_records: number;
  auto_records?: number;
  last_activity_at?: number;
}

interface RecentRun {
  source_system_id: string;
  source_name: string;
  run_type: string;
  run_label: string;
  total_records: number;
  review_records: number;
  matched_records: number;
  rejected_records: number;
  updated_at?: number;
}

interface DashboardMetrics {
  inbound_records: number;
  awaiting_review: number;
  auto_merged: number;
  golden_suppliers: number;
  rejected_outliers: number;
  data_freshness_at?: number | null;
}

interface NotificationItem {
  id: string;
  tone: string;
  title: string;
  body: string;
  created_at?: number;
}

const TENANT_ID = 'tenant-hospitality-demo';

const fallbackMetrics: DashboardMetrics = {
  inbound_records: 3000,
  awaiting_review: 750,
  auto_merged: 1700,
  golden_suppliers: 1009,
  rejected_outliers: 227,
  data_freshness_at: 1779926400,
};

const fallbackSuppliers: Supplier[] = [
  {
    datax_id: 'SUP-MSTR-0001',
    master_legal_name: 'Magic City Beverage LLC',
    master_tax_id: '93-978-1519',
    master_duns_number: '93-978-1519',
    master_website: 'www.magiccitybeverage.com',
    tenant_supplier_code: 'SUP-MSTR-0001',
    tenant_status: 'active',
    tenant_classification: 'preferred',
    data_quality_score: 89,
    city: 'Birmingham',
    state_province: 'Alabama',
    country: 'USA',
    xref_count: 4,
  },
  {
    datax_id: 'DATAX-ACME-001',
    master_legal_name: 'Acme Corporation',
    master_tax_id: '31-551-1099',
    master_duns_number: '11-482-5530',
    master_website: 'www.acmecorp.example',
    tenant_supplier_code: 'DATAX-ACME-001',
    tenant_status: 'active',
    tenant_classification: 'preferred',
    data_quality_score: 84,
    city: 'Atlanta',
    state_province: 'Georgia',
    country: 'USA',
    xref_count: 2,
  },
  {
    datax_id: 'dx-supplier-001',
    master_legal_name: 'Advanced Manufacturing Inc',
    master_tax_id: '44-892-1187',
    master_duns_number: '65-344-1029',
    master_website: 'www.advancedmanufacturing.example',
    tenant_supplier_code: 'dx-supplier-001',
    tenant_status: 'active',
    tenant_classification: 'strategic',
    data_quality_score: 82,
    city: 'Detroit',
    state_province: 'Michigan',
    country: 'USA',
    xref_count: 3,
  },
  {
    datax_id: 'SUP-MSTR-0901',
    master_legal_name: 'Continental Grain Holdings',
    master_tax_id: '22-8841102',
    master_duns_number: '44-190-8330',
    master_website: 'www.continentalgrainholdings.example',
    tenant_supplier_code: 'SUP-MSTR-0901',
    tenant_status: 'active',
    tenant_classification: 'strategic',
    data_quality_score: 91,
    city: 'Wilmington',
    state_province: 'Delaware',
    country: 'USA',
    xref_count: 7,
  },
  { datax_id: 'SUP-MSTR-0242', master_legal_name: 'Bayou Bakery Cooperative', tenant_supplier_code: 'SUP-MSTR-0242', tenant_status: 'active', tenant_classification: 'preferred', data_quality_score: 78, city: 'New Orleans', state_province: 'Louisiana', country: 'USA', xref_count: 0 },
  { datax_id: 'SUP-MSTR-0286', master_legal_name: 'Bayou Baking Corp', tenant_supplier_code: 'SUP-MSTR-0286', tenant_status: 'active', tenant_classification: 'preferred', data_quality_score: 76, city: 'Lafayette', state_province: 'Louisiana', country: 'USA', xref_count: 1 },
  { datax_id: 'SUP-MSTR-0053', master_legal_name: 'Bayou Baking Inc', tenant_supplier_code: 'SUP-MSTR-0053', tenant_status: 'active', tenant_classification: 'preferred', data_quality_score: 74, city: 'Baton Rouge', state_province: 'Louisiana', country: 'USA', xref_count: 0 },
  { datax_id: 'SUP-MSTR-0488', master_legal_name: 'Bayou Beverage Corp', tenant_supplier_code: 'SUP-MSTR-0488', tenant_status: 'active', tenant_classification: 'preferred', data_quality_score: 81, city: 'Mobile', state_province: 'Alabama', country: 'USA', xref_count: 2 },
  { datax_id: 'SUP-MSTR-0717', master_legal_name: 'Bayou Beverage LLC', tenant_supplier_code: 'SUP-MSTR-0717', tenant_status: 'active', tenant_classification: 'preferred', data_quality_score: 86, city: 'Birmingham', state_province: 'Alabama', country: 'USA', xref_count: 2 },
  { datax_id: 'SUP-MSTR-0523', master_legal_name: 'Bayou Chemical Supply Cooperative', tenant_supplier_code: 'SUP-MSTR-0523', tenant_status: 'active', tenant_classification: 'tactical', data_quality_score: 79, city: 'Lake Charles', state_province: 'Louisiana', country: 'USA', xref_count: 1 },
  { datax_id: 'SUP-MSTR-0788', master_legal_name: 'Global Logistics Cooperative', tenant_supplier_code: 'SUP-MSTR-0788', tenant_status: 'active', tenant_classification: 'strategic', data_quality_score: 87, city: 'Savannah', state_province: 'Georgia', country: 'USA', xref_count: 4 },
  { datax_id: 'SUP-MSTR-0346', master_legal_name: 'Horizon Logistics Group', tenant_supplier_code: 'SUP-MSTR-0346', tenant_status: 'active', tenant_classification: 'preferred', data_quality_score: 83, city: 'Dallas', state_province: 'Texas', country: 'USA', xref_count: 3 },
  { datax_id: 'SUP-MSTR-0649', master_legal_name: 'Nexus Janitorial Corp', tenant_supplier_code: 'SUP-MSTR-0649', tenant_status: 'active', tenant_classification: 'tactical', data_quality_score: 75, city: 'Portland', state_province: 'Oregon', country: 'USA', xref_count: 1 },
];

const fallbackSources: Record<string, SourceRecord[]> = {
  'SUP-MSTR-0001': [
    { source_record_id: 'XRF-0011', source_name: 'AP Inbox', document_type: 'Invoice', document_id: 'INV-2026-04118', source_supplier_name: 'Magic City Beverage LLC', resolved_entity: 'Magic City Beverage LLC', gl_code: 'GL-2410 Ingredients', source_tax_id: '93-978-1519', source_address: '4821 Commerce Blvd, Birmingham, AL', match_score: 0.96, merge_method: 'auto', evidence: 'Tax ID, remit address, and invoice header matched the master record.', match_summary: 'Invoice resolved to supplier and ingredients GL code.' },
    { source_record_id: 'XRF-0012', source_name: 'Procurement Inbox', document_type: 'Purchase order', document_id: 'PO-880142', source_supplier_name: 'Magic City Beverage', resolved_entity: 'Magic City Beverage LLC', gl_code: 'GL-8040 Logistics', source_address: 'Birmingham AL 35234', match_score: 0.91, merge_method: 'auto', evidence: 'PO supplier name variant and ship-from location matched accepted aliases.', match_summary: 'Purchase order mapped to canonical entity before posting.' },
    { source_record_id: 'XRF-0013', source_name: 'Email Intake', document_type: 'W-9', document_id: 'W9-2026-0097', source_supplier_name: 'Magic City Bev LLC', resolved_entity: 'Magic City Beverage LLC', gl_code: 'Not applicable', source_tax_id: '93-978-1519', source_address: 'Commerce Blvd', match_score: 0.78, merge_method: 'steward review', evidence: 'Tax ID matched; abbreviated legal name required steward approval.', match_summary: 'Tax document linked as supporting evidence, not a spend posting.' },
    { source_record_id: 'XRF-0014', source_name: 'Contract Inbox', document_type: 'Agreement', document_id: 'MSA-2026-017', source_supplier_name: 'Magic City Beverage - South', resolved_entity: 'Magic City Beverage LLC', gl_code: 'GL-7310 Distributor services', match_score: 0.72, merge_method: 'steward review', evidence: 'Regional DBA was mapped to the parent supplier entity.', match_summary: 'Regional agreement linked to canonical master supplier.' },
  ],
  'DATAX-ACME-001': [
    { source_record_id: 'XRF-0021', source_name: 'AP Inbox', document_type: 'Invoice', document_id: 'INV-2026-03902', source_supplier_name: 'Acme Corporation', resolved_entity: 'Acme Corporation', gl_code: 'GL-6200 Packaging materials', source_tax_id: '31-551-1099', source_address: 'Atlanta, GA', match_score: 0.97, merge_method: 'auto', evidence: 'Tax ID and payment remit address matched.' },
    { source_record_id: 'XRF-0022', source_name: 'Procurement Inbox', document_type: 'Purchase order', document_id: 'PO-779513', source_supplier_name: 'Acme Corp', resolved_entity: 'Acme Corporation', gl_code: 'GL-6235 Corrugate', source_address: 'Peachtree Street', match_score: 0.88, merge_method: 'steward review', evidence: 'Alias matched but PO category needed GL confirmation.' },
  ],
  'dx-supplier-001': [
    { source_record_id: 'XRF-0031', source_name: 'AP Inbox', document_type: 'Invoice', document_id: 'INV-2026-04221', source_supplier_name: 'Advanced Manufacturing Inc', resolved_entity: 'Advanced Manufacturing Inc', gl_code: 'GL-5100 Direct materials', source_tax_id: '44-892-1187', source_address: 'Detroit, MI', match_score: 0.95, merge_method: 'auto', evidence: 'Invoice remittance and tax ID matched.' },
    { source_record_id: 'XRF-0032', source_name: 'Procurement Inbox', document_type: 'Purchase order', document_id: 'PO-904221', source_supplier_name: 'Advanced Mfg', resolved_entity: 'Advanced Manufacturing Inc', gl_code: 'GL-5150 Tooling', source_address: 'Jefferson Ave', match_score: 0.74, merge_method: 'steward review', evidence: 'Supplier alias and plant address matched with lower confidence.' },
    { source_record_id: 'XRF-0033', source_name: 'Email Intake', document_type: 'Certificate', document_id: 'CERT-ISO-2026-11', source_supplier_name: 'Advanced Manufacturing International', resolved_entity: 'Advanced Manufacturing Inc', gl_code: 'Not applicable', source_tax_id: '44-892-1187', source_address: 'Detroit MI', match_score: 0.87, merge_method: 'steward review', evidence: 'Certificate was linked as entity evidence after tax ID verification.' },
  ],
};

const fallbackReviews: ReviewItem[] = [
  {
    review_id: 'review-0001',
    source_record_id: 'XRF-000939',
    source_supplier_name: "Vera's Burger Joint Supply Chain",
    source_name: 'ERP-SAP',
    candidate_datax_id: 'SUP-MSTR-0523',
    candidate_name: 'Bayou Chemical Supply Cooperative',
    match_score: 0.88,
    assigned_to_user_id: 'tu-001',
    assigned_to_name: 'Jane King',
    assigned_to_email: 'jane.king@example.com',
    comments: 'Similar address and procurement category; steward approval required.',
    created_at: 1776200400,
  },
  {
    review_id: 'review-0002',
    source_record_id: 'XRF-000719',
    source_supplier_name: 'Peach State Packaging Solutions',
    source_name: 'ERP-Oracle',
    candidate_datax_id: 'SUP-MSTR-0788',
    candidate_name: 'Global Logistics Cooperative',
    match_score: 0.88,
    assigned_to_user_id: 'tu-003',
    assigned_to_name: 'Marcus Rivera',
    comments: 'Name similarity is high but source category conflicts with logistics master.',
    created_at: 1778396400,
  },
  {
    review_id: 'review-0003',
    source_record_id: 'XRF-000504',
    source_supplier_name: 'Big Sky Montana Grain Co',
    source_name: 'Procurement',
    candidate_datax_id: 'SUP-MSTR-0717',
    candidate_name: 'Bayou Beverage LLC',
    match_score: 0.86,
    comments: 'Shared DUNS family but address differs.',
    created_at: 1776834000,
  },
  {
    review_id: 'review-0004',
    source_record_id: 'XRF-001432',
    source_supplier_name: 'Mackinac Island Fudge & Confectionery',
    source_name: 'Vendor Portal',
    candidate_datax_id: 'SUP-MSTR-0574',
    candidate_name: 'Bayou Supply Co Cooperative',
    match_score: 0.85,
    comments: 'High name similarity to an existing co-op supplier.',
    created_at: 1772514000,
  },
  {
    review_id: 'review-0005',
    source_record_id: 'XRF-000718',
    source_supplier_name: 'Shamrock Foods Company',
    source_name: 'ERP-SAP',
    candidate_datax_id: 'SUP-MSTR-0346',
    candidate_name: 'Horizon Logistics Group',
    match_score: 0.83,
    comments: 'Identifier match is partial; confidence is below auto-link threshold.',
    created_at: 1776315600,
  },
  {
    review_id: 'review-0006',
    source_record_id: 'XRF-000016',
    source_supplier_name: 'Silicon Forest Tech Canteen',
    source_name: 'ERP-Oracle',
    candidate_datax_id: 'SUP-MSTR-0649',
    candidate_name: 'Nexus Janitorial Corp',
    match_score: 0.81,
    assigned_to_user_id: 'tu-002',
    assigned_to_name: 'David Brown',
    comments: 'Location and category signals conflict.',
    created_at: 1775883600,
  },
];

const fallbackConnections: ConnectionSummary[] = [
  { source_system_id: 'sap', source_name: 'ERP-SAP', connection_type: 'API', trust_score: 0.95, status: 'active', total_records: 900, review_records: 225, matched_records: 510, rejected_records: 68, new_records: 72, auto_records: 510, last_activity_at: 1779926400 },
  { source_system_id: 'oracle', source_name: 'ERP-Oracle', connection_type: 'SFTP', trust_score: 0.95, status: 'active', total_records: 600, review_records: 150, matched_records: 340, rejected_records: 45, new_records: 48, auto_records: 340, last_activity_at: 1779926400 },
  { source_system_id: 'portal', source_name: 'Vendor Portal', connection_type: 'API', trust_score: 0.78, status: 'active', total_records: 600, review_records: 150, matched_records: 340, rejected_records: 45, new_records: 48, auto_records: 340, last_activity_at: 1779926400 },
  { source_system_id: 'ap', source_name: 'AP Legacy', connection_type: 'Manual', trust_score: 0.68, status: 'watch', total_records: 450, review_records: 112, matched_records: 255, rejected_records: 35, new_records: 36, auto_records: 255, last_activity_at: 1779840000 },
  { source_system_id: 'procurement', source_name: 'Procurement', connection_type: 'Mailbox', trust_score: 0.72, status: 'active', total_records: 450, review_records: 113, matched_records: 255, rejected_records: 34, new_records: 36, auto_records: 255, last_activity_at: 1779926400 },
];

const fallbackRecentRuns: RecentRun[] = [
  { source_system_id: 'sap', source_name: 'ERP-SAP', run_type: 'API', run_label: 'SAP nightly sync', total_records: 900, review_records: 161, matched_records: 329, rejected_records: 22, updated_at: 1779926400 },
  { source_system_id: 'oracle', source_name: 'ERP-Oracle', run_type: 'SFTP', run_label: 'Oracle SFTP drop', total_records: 600, review_records: 114, matched_records: 203, rejected_records: 18, updated_at: 1779926400 },
  { source_system_id: 'portal', source_name: 'Vendor Portal', run_type: 'API', run_label: 'Vendor Portal API', total_records: 600, review_records: 112, matched_records: 206, rejected_records: 22, updated_at: 1779926400 },
  { source_system_id: 'ap', source_name: 'AP Legacy', run_type: 'Manual', run_label: 'Manual CSV upload', total_records: 450, review_records: 89, matched_records: 152, rejected_records: 31, updated_at: 1779840000 },
  { source_system_id: 'procurement', source_name: 'Procurement', run_type: 'Mailbox', run_label: 'Procurement mailbox', total_records: 450, review_records: 84, matched_records: 119, rejected_records: 24, updated_at: 1779926400 },
];

const fallbackActivities: Activity[] = [
  { source_record_id: 'XRF-002204', source_supplier_name: 'Infinite Spiral Pasta Co', source_name: 'ERP-SAP', record_status: 'pending', queue_state: 'new', run_label: 'SAP nightly sync', run_type: 'API', updated_at: 1778803200 },
  { source_record_id: 'XRF-000152', source_supplier_name: 'charm city cleaning solutions - East Coast Div.', source_name: 'ERP-Oracle', record_status: 'matched', queue_state: 'matched', match_score: 0.91, master_legal_name: 'Charm City Cleaning Solutions', datax_id: 'SUP-MSTR-0042', run_label: 'Oracle SFTP drop', run_type: 'SFTP', updated_at: 1778803200 },
  { source_record_id: 'XRF-002888', source_supplier_name: 'River City BBQ & Catering Pits', source_name: 'ERP-Oracle', record_status: 'review', queue_state: 'review', match_score: 0.78, master_legal_name: 'River City Catering Group', datax_id: 'SUP-MSTR-0291', run_label: 'Oracle SFTP drop', run_type: 'SFTP', updated_at: 1778803200 },
  { source_record_id: 'XRF-000836', source_supplier_name: 'Orbit Supply', source_name: 'Procurement', record_status: 'review', queue_state: 'review', match_score: 0.82, master_legal_name: 'Orbit Supply Co', datax_id: 'SUP-MSTR-0405', run_label: 'Procurement mailbox', run_type: 'Mailbox', updated_at: 1778803200 },
  { source_record_id: 'XRF-001672', source_supplier_name: 'Synergy Hospitality Products Group', source_name: 'Vendor Portal', record_status: 'matched', queue_state: 'matched', match_score: 0.94, master_legal_name: 'Synergy Hospitality Products', datax_id: 'SUP-MSTR-0187', run_label: 'Vendor Portal API', run_type: 'API', updated_at: 1778803200 },
  { source_record_id: 'XRF-001520', source_supplier_name: 'Guest Supply LLC', source_name: 'AP Legacy', record_status: 'pending', queue_state: 'new', run_label: 'Manual CSV upload', run_type: 'Manual', updated_at: 1778803200 },
  { source_record_id: 'XRF-002356', source_supplier_name: 'Peach', source_name: 'Procurement', record_status: 'matched', queue_state: 'matched', match_score: 0.88, master_legal_name: 'Peach State Packaging Solutions', datax_id: 'SUP-MSTR-0788', run_label: 'Procurement mailbox', run_type: 'Mailbox', updated_at: 1778803200 },
  { source_record_id: 'XRF-000939', source_supplier_name: "Vera's Burger Joint Supply Chain", source_name: 'ERP-SAP', record_status: 'review', queue_state: 'review', match_score: 0.88, master_legal_name: 'Bayou Chemical Supply Cooperative', datax_id: 'SUP-MSTR-0523', run_label: 'SAP nightly sync', run_type: 'API', updated_at: 1778803200 },
  { source_record_id: 'XRF-001432', source_supplier_name: 'Mackinac Island Fudge & Confectionery', source_name: 'Vendor Portal', record_status: 'review', queue_state: 'review', match_score: 0.85, master_legal_name: 'Bayou Supply Co Cooperative', datax_id: 'SUP-MSTR-0574', run_label: 'Vendor Portal API', run_type: 'API', updated_at: 1778803200 },
  { source_record_id: 'XRF-002374', source_supplier_name: 'Corrupted Address Row', source_name: 'AP Legacy', record_status: 'outlier', queue_state: 'outlier', comments: 'Excluded from learning because the address payload is malformed.', run_label: 'Manual CSV upload', run_type: 'Manual', updated_at: 1778803200 },
  { source_record_id: 'XRF-001102', source_supplier_name: 'Duplicate Vendor LLC', source_name: 'Procurement', record_status: 'rejected', queue_state: 'rejected', comments: 'Confirmed duplicate already linked as XRF-000152.', run_label: 'Procurement mailbox', run_type: 'Mailbox', updated_at: 1778716800 },
  { source_record_id: 'XRF-003001', source_supplier_name: 'Failed Import Row', source_name: 'ERP-SAP', record_status: 'failed', queue_state: 'failed', comments: 'Failed validation: missing required legal name.', run_label: 'SAP nightly sync', run_type: 'API', updated_at: 1778716800 },
];

const fallbackSignals: MatchSignal[] = [
  { label: 'Tax ID', score: 1, weight: 0.28, detail: 'Exact tax identifier match' },
  { label: 'Supplier name', score: 0.58, weight: 0.22, detail: '58% normalized legal/DBA name similarity' },
  { label: 'Address', score: 0.22, weight: 0.14, detail: 'Address differs from HQ and requires review' },
  { label: 'Source trust', score: 0.95, weight: 0.06, detail: '95% trust for ERP-SAP' },
];

function fallbackProfileData(supplier?: Partial<Supplier>): SupplierProfileData {
  const id = supplier?.datax_id || 'SUP-MSTR-0001';
  const name = supplier?.master_legal_name || 'Supplier';
  const base = Math.abs(id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0));
  const riskScore = 18 + (base % 48);
  const esgScore = 54 + (base % 39);
  const certSuffix = id.replace(/\D/g, '').slice(-4) || '0001';
  return {
    certifications: [
      { supplier_certification_id: `cert-${id}-iso`, certification_name: 'ISO 9001 Quality Management', certification_level: 'ISO 9001:2015', certifying_body: 'DNV', certificate_number: `ISO-${certSuffix}`, expiration_date: 1809216000, status: 'active' },
      { supplier_certification_id: `cert-${id}-food`, certification_name: 'Food Safety / HACCP', certification_level: 'HACCP verified', certifying_body: 'NSF', certificate_number: `HACCP-${certSuffix}`, expiration_date: 1817164800, status: 'active' },
    ],
    esg_scores: [
      { esg_score_id: `esg-${id}`, overall_score: esgScore, environmental_score: esgScore - 3, social_score: esgScore + 2, governance_score: esgScore - 1, scoring_agency: 'EcoVadis', rating_level: esgScore > 78 ? 'Gold' : esgScore > 65 ? 'Silver' : 'Bronze', valid_until: 1811894400 },
    ],
    environmental: [
      { environmental_id: `env-${id}`, reporting_year: 2025, carbon_footprint_scope1: 1200 + base, carbon_footprint_scope2: 860 + base, renewable_energy_percentage: 24 + (base % 52), waste_recycled_percentage: 42 + (base % 37), reporting_standard: 'GRI' },
    ],
    risk_assessments: [
      { risk_assessment_id: `risk-${id}`, overall_risk_score: riskScore, overall_risk_rating: riskScore > 58 ? 'medium' : 'low', financial_risk_score: riskScore + 4, operational_risk_score: riskScore + 7, cybersecurity_risk_score: riskScore - 2, geopolitical_risk_score: riskScore - 8, compliance_risk_score: riskScore - 5, esg_risk_score: Math.max(10, 100 - esgScore), assessment_date: 1777593600, next_assessment_date: 1809129600 },
    ],
    diversity: [
      { diversity_id: `div-${id}`, classification_type: base % 2 ? 'WBE' : 'SBE', certifying_agency: 'NMSDC / WBENC partner registry', certificate_number: `DIV-${certSuffix}`, expiration_date: 1814486400, verification_status: 'verified' },
    ],
    compliance: [
      { compliance_id: `comp-${id}-sanctions`, compliance_type: 'sanctions', regulation_reference: 'OFAC / UN / EU', compliance_status: 'compliant', last_verified_date: 1780099200, verified_by: 'Screening provider' },
      { compliance_id: `comp-${id}-privacy`, compliance_type: 'data_privacy', regulation_reference: 'GDPR / CCPA supplier controls', compliance_status: 'under_review', last_verified_date: 1777593600, verified_by: 'Procurement compliance' },
    ],
    sanctions: [
      { screening_id: `screen-${id}`, screening_provider: 'Dow Jones Risk Center', match_status: 'clear', lists_checked: 'OFAC, UN, EU, HMT', risk_level: 'low', screening_date: 1780099200 },
    ],
    performance: [
      { performance_id: `perf-${id}`, overall_score: 4.2, quality_score: 4.4, delivery_score: 4.1, responsiveness_score: 4.0, commercial_score: 4.2, on_time_delivery_percentage: 94 - (base % 8), defect_rate_ppm: 90 + (base % 120), avg_response_time_hours: 12 + (base % 18), total_pos: 140 + (base % 240), total_spend: 1250000 + (base * 900), spend_currency: 'USD', evaluation_date: 1777593600 },
    ],
    documents: [
      { document_id: `doc-${id}-w9`, document_type: 'tax', document_name: `${name} W-9`, file_path: `/documents/${id}/w9.pdf`, file_size: 184000, mime_type: 'application/pdf', uploaded_date: 1775001600, expiration_date: null },
      { document_id: `doc-${id}-insurance`, document_type: 'insurance', document_name: 'Certificate of Insurance', file_path: `/documents/${id}/insurance.pdf`, file_size: 312000, mime_type: 'application/pdf', uploaded_date: 1777593600, expiration_date: 1811894400 },
    ],
    relationships: [],
    contacts: [],
    locations: [],
  };
}

function getGsrScenario(selected: Supplier) {
  const id = selected.datax_id;
  const supplierBase = (selected.master_legal_name || 'Supplier').replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase() || 'SUPP';
  const aliasStem = selected.master_legal_name?.split(' ')[0] || 'Supplier';
  const certPool = ['ISO 9001', 'HACCP', 'SOC 2', 'ISO 14001', 'GFSI'];
  const catPool = ['Packaging', 'Cold chain', 'Ingredients', 'Logistics', 'Hospitality supplies'];
  const hash = Array.from(id).reduce((a, c) => a + c.charCodeAt(0), 0);
  const defaultScenario = {
    overview: {
      tier: selected.tenant_classification || 'preferred',
      paymentTerms: hash % 3 === 0 ? 'Net 30' : hash % 3 === 1 ? 'Net 45' : 'Net 60',
      ndaStatus: hash % 4 === 0 ? 'Renewal due in 30 days' : 'Active',
      msaStatus: hash % 5 === 0 ? 'Under review' : 'Active',
      completeness: selected.data_quality_score || 72,
      missingFields: ['LEI visibility', 'Secondary banking details'],
      needsAttention: hash % 2 ? ['Insurance certificate expires in < 45 days.'] : ['Address normalization pending from one source feed.'],
      contactName: `${aliasStem} Supplier Desk`,
      contactEmail: `supplierdesk@${(selected.master_website || 'example.com').replace(/^https?:\/\//, '').replace(/^www\./, '')}`,
      contactPhone: '+1 (281) 555-0102',
      hq: [selected.city, selected.state_province, selected.country].filter(Boolean).join(', ') || 'Unavailable',
      registry: { number: `US-${supplierBase}-${String(1000 + (hash % 8999))}`, type: hash % 2 ? 'Limited Liability Company' : 'Corporation', jurisdiction: 'US', flag: 'US', incorporated: 'Jan 1, 2010', branchStatus: hash % 3 ? 'Home company' : 'Branch', homeCompany: hash % 3 ? '—' : 'Global Parent Holdings' },
      freshness: { source: 'OpenCorporates + State Registry', lastUpdated: 'May 15, 2026', lastChange: 'Apr 21, 2026', nextUpdate: 'Jun 15, 2026', badge: 'Current' },
      identifiers: [
        { label: 'DUNS', value: selected.master_duns_number || '-', restricted: false },
        { label: 'EIN', value: selected.master_tax_id || '-', restricted: true },
        { label: 'Company Reg', value: `US-${supplierBase}-${String(1000 + (hash % 8999))}`, restricted: false },
        { label: 'LEI', value: '5493001KJTIIGC8Y1R12', restricted: true },
        { label: 'OpenCorporates ID', value: 'us_de/123456', restricted: false },
      ],
      officers: {
        active: [
          { name: 'Jordan Blake', role: 'Managing Director', appointed: 'Jan 14, 2020', registry: 'OpenCorporates' },
          { name: 'Avery Singh', role: 'Chief Financial Officer', appointed: 'Jun 1, 2021', registry: 'State Registry' },
          { name: 'Taylor Chen', role: 'Secretary', appointed: 'Sep 18, 2019', registry: 'State Registry' },
        ],
        ceased: [
          { name: 'Morgan Hale', role: 'Director', appointed: 'Mar 1, 2012', ceased: 'Dec 30, 2018', registry: 'State Registry' },
        ] as any[],
      },
    },
    names: [
      { type: 'Legal name', value: selected.master_legal_name, from: 'Jan 1, 2010', to: 'Current' },
      { type: 'DBA', value: `${aliasStem} Supply`, from: 'Jan 1, 2012', to: 'Current' },
      { type: 'Trade name', value: `${aliasStem} Group`, from: 'Jan 1, 2015', to: 'Current' },
      { type: 'Former name', value: `${aliasStem} Distribution Inc`, from: 'Jan 1, 2008', to: 'Dec 31, 2014' },
      { type: 'Abbreviation', value: aliasStem.slice(0, 3).toUpperCase(), from: 'Jan 1, 2010', to: 'Current' },
    ],
    relationships: {
      parent: hash % 3 ? 'No parent recorded' : 'Global Parent Holdings',
      subsidiaries: hash % 2 ? [`${aliasStem} West`, `${aliasStem} East`] : [`${aliasStem} Services`],
      network: ['JV: Regional Procurement Alliance', 'Competitor: Harbor Supply Group'],
      ma: hash % 2 ? ['2020 acquisition of local distributor'] : ['2019 corporate rebrand'],
    },
    agreements: {
      nda: hash % 4 === 0 ? 'Renewal due in 30 days' : 'Active',
      msa: hash % 5 === 0 ? 'Under review' : 'Active',
      preferredTier: selected.tenant_classification || 'preferred',
      paymentTerms: hash % 3 === 0 ? 'Net 30' : hash % 3 === 1 ? 'Net 45' : 'Net 60',
      categories: [
        { name: catPool[hash % catPool.length], spend: 42 + (hash % 40) },
        { name: catPool[(hash + 2) % catPool.length], spend: 30 + (hash % 30) },
      ],
      insurance: [{ name: 'General Liability', expires: hash % 2 ? 'Jul 12, 2026' : 'Jan 9, 2027', state: hash % 2 ? 'expiring_soon' : 'active' }],
      certifications: [certPool[hash % certPool.length], certPool[(hash + 1) % certPool.length]],
      poHistory: { total: 120 + (hash % 240), onTime: `${88 + (hash % 10)}%`, avgCycle: `${2 + (hash % 5)}.${hash % 9} days` },
    },
    filings: [
      { date: 'Apr 1, 2023', type: 'Annual report', source: 'State registry' },
      { date: 'Apr 1, 2024', type: 'Annual report', source: 'State registry' },
      { date: 'Apr 1, 2025', type: 'Annual report', source: 'State registry' },
    ],
  };

  if (id === 'SUP-MSTR-0001') {
    return {
      ...defaultScenario,
      overview: {
        ...defaultScenario.overview,
        tier: 'preferred',
        paymentTerms: 'Net 30',
        completeness: 89,
        missingFields: ['LEI visibility', 'Board members'],
        needsAttention: ['COI renewal due in 14 days.', 'Tax profile update pending from AP source.'],
        registry: { number: '0801234567', type: 'Limited Liability Company', jurisdiction: 'Alabama, US', flag: 'US', incorporated: 'Mar 12, 2001', branchStatus: 'Home company', homeCompany: '—' },
        freshness: { source: 'Alabama Secretary of State + OpenCorporates', lastUpdated: 'May 28, 2026', lastChange: 'Jan 12, 2026', nextUpdate: 'Jun 28, 2026', badge: 'Current' },
        identifiers: [
          { label: 'DUNS', value: selected.master_duns_number || '93-978-1519', restricted: false },
          { label: 'EIN', value: selected.master_tax_id || '93-978-1519', restricted: true },
          { label: 'Company Reg', value: '0801234567', restricted: false },
          { label: 'LEI', value: '549300MCBLLC2001AL01', restricted: true },
          { label: 'OpenCorporates ID', value: 'us_al/20018842', restricted: false },
        ],
        officers: {
          active: [
            { name: 'Jessica Kim', role: 'Managing Director', appointed: 'Jan 14, 2020', registry: 'Alabama SOS' },
            { name: 'Marcus Rivera', role: 'Chief Financial Officer', appointed: 'Jun 1, 2021', registry: 'Alabama SOS' },
            { name: 'Elena Park', role: 'Secretary', appointed: 'Sep 18, 2019', registry: 'OpenCorporates' },
            { name: 'David Brown', role: 'Operations Director', appointed: 'Feb 12, 2022', registry: 'Alabama SOS' },
          ],
          ceased: [
            { name: 'Thomas Wade', role: 'Director', appointed: 'Mar 1, 2012', ceased: 'Dec 30, 2018', registry: 'Alabama SOS' },
          ],
        },
      },
      names: [
        { type: 'Legal name', value: 'Magic City Beverage LLC', from: 'May 4, 2008', to: 'Current' },
        { type: 'Former name', value: 'Magic City Beverage Company', from: 'Mar 12, 2001', to: 'May 3, 2008' },
        { type: 'DBA', value: 'Magic City Beverage South', from: 'Aug 1, 2019', to: 'Current' },
        { type: 'Abbreviation', value: 'MCB', from: 'May 4, 2008', to: 'Current' },
      ],
      relationships: {
        parent: 'Continental Grain Holdings',
        subsidiaries: ['Magic City Beverage South Distribution', 'Magic City Cold Chain Services'],
        network: ['JV: Bayou Procurement Cooperative', 'Competitor: Peach State Packaging Solutions'],
        ma: ['2008 Rebrand to Magic City Beverage LLC', '2021 Minority acquisition of Cold Chain Services'],
      },
      agreements: {
        ...defaultScenario.agreements,
        nda: 'Active · renew Nov 2026',
        msa: 'Active · auto-renew',
        categories: [{ name: 'Beverage concentrates', spend: 74 }, { name: 'Packaging', spend: 63 }, { name: 'Cold chain services', spend: 48 }],
      },
      filings: [
        { date: 'Mar 12, 2001', type: 'Certificate of Formation', source: 'Alabama Secretary of State' },
        { date: 'May 4, 2008', type: 'Name change amendment', source: 'Alabama Secretary of State' },
        { date: 'Apr 1, 2023', type: 'Annual report', source: 'Alabama Secretary of State' },
        { date: 'Apr 1, 2024', type: 'Annual report', source: 'Alabama Secretary of State' },
        { date: 'Apr 1, 2025', type: 'Annual report', source: 'Alabama Secretary of State' },
      ],
    };
  }
  if (id === 'DATAX-ACME-001') {
    return {
      ...defaultScenario,
      overview: {
        ...defaultScenario.overview,
        completeness: 84,
        registry: { number: 'DE-7784201', type: 'Corporation', jurisdiction: 'Delaware, US', flag: 'US', incorporated: 'Feb 18, 1997', branchStatus: 'Branch', homeCompany: 'Acme Global Holdings Inc' },
      },
      relationships: {
        parent: 'Acme Global Holdings Inc',
        subsidiaries: ['Acme Corporation - Southeast Branch'],
        network: ['Competitor: Bayou Chemical Supply Cooperative'],
        ma: ['2018 acquired by Acme Global Holdings Inc'],
      },
    };
  }
  if (selected.master_legal_name.toLowerCase().includes('continental')) {
    return {
      ...defaultScenario,
      overview: {
        ...defaultScenario.overview,
        tier: 'strategic',
        paymentTerms: 'Net 60',
        ndaStatus: 'Active',
        msaStatus: 'Active',
        completeness: 93,
        missingFields: ['Ultimate beneficial owners'],
        needsAttention: ['LEI entitlement renewal in 30 days.'],
        registry: { number: 'DE-1908330', type: 'Holding company', jurisdiction: 'Delaware, US', flag: 'US', incorporated: 'Jun 9, 1984', branchStatus: 'Home company', homeCompany: '—' },
        freshness: { source: 'Delaware Registry + OpenCorporates', lastUpdated: 'May 29, 2026', lastChange: 'Mar 4, 2026', nextUpdate: 'Jun 29, 2026', badge: 'Current' },
        officers: {
          active: [
            { name: 'Charlotte Boyd', role: 'CEO', appointed: 'Aug 10, 2018', registry: 'Delaware DoC' },
            { name: 'Evan Holt', role: 'CFO', appointed: 'Aug 10, 2018', registry: 'OpenCorporates' },
          ],
          ceased: [],
        },
      },
      names: [
        { type: 'Legal name', value: 'Continental Grain Holdings', from: 'Jun 9, 1984', to: 'Current' },
        { type: 'Former name', value: 'Continental Grain Group', from: 'Jun 9, 1984', to: 'Sep 14, 2002' },
        { type: 'Trade name', value: 'Continental Holdings', from: 'Sep 15, 2002', to: 'Current' },
        { type: 'Abbreviation', value: 'CGH', from: 'Sep 15, 2002', to: 'Current' },
      ],
      relationships: {
        parent: 'No parent recorded',
        subsidiaries: ['Magic City Beverage LLC', 'Continental Packaging Network', 'CGH Procurement Services'],
        network: ['Competitor: Acme Global Holdings Inc'],
        ma: ['2002 corporate rename and restructuring', '2021 merger of two regional grain entities'],
      },
      agreements: {
        ...defaultScenario.agreements,
        nda: 'Active · renew Feb 2027',
        msa: 'Active · custom clause set',
        paymentTerms: 'Net 60',
        categories: [{ name: 'Grain commodities', spend: 82 }, { name: 'Logistics', spend: 57 }],
        insurance: [{ name: 'Global Liability Umbrella', expires: 'Jan 5, 2027', state: 'active' }],
        certifications: ['SOC 2', 'ISO 28000'],
        poHistory: { total: 311, onTime: '97%', avgCycle: '4.1 days' },
      },
      filings: [
        { date: 'Jun 9, 1984', type: 'Certificate of Incorporation', source: 'Delaware Division of Corporations' },
        { date: 'Sep 15, 2002', type: 'Name change filing', source: 'Delaware Division of Corporations' },
        { date: 'Mar 30, 2023', type: 'Annual report', source: 'Delaware Division of Corporations' },
        { date: 'Mar 30, 2024', type: 'Annual report', source: 'Delaware Division of Corporations' },
        { date: 'Mar 30, 2025', type: 'Annual report', source: 'Delaware Division of Corporations' },
      ],
    };
  }
  return defaultScenario;
}

function fmtScore(score?: number) {
  if (score === undefined || score === null) return '-';
  if (score === 0) return '-';
  return score.toFixed(2);
}

function formatDate(timestamp?: number) {
  if (!timestamp) return 'Oct 30, 2025';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function announceAction(message: string) {
  window.dispatchEvent(new CustomEvent('supplier-master-action', { detail: message }));
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const response = await fetch(path, {
    ...options,
    headers: {
      ...(!isFormData && options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const message = await response.text();
    let errorMessage = message || response.statusText || 'Request failed';
    try {
      const parsed = JSON.parse(message);
      errorMessage = parsed.error || parsed.message || errorMessage;
    } catch {
      // Keep plain-text errors as-is.
    }
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth-expired', { detail: errorMessage }));
      throw new Error('Session expired. Please sign in again.');
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

function userFromJwt(token: string) {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
    const primaryTenant = decoded.tenants?.[0] || {};
    return {
      user_id: decoded.user_id,
      email: decoded.email,
      full_name: decoded.name || decoded.email,
      is_super_admin: decoded.is_super_admin === true,
      tenant_id: decoded.tenant_id || primaryTenant.tenant_id,
      role: decoded.role || primaryTenant.role,
      tenants: decoded.tenants || [],
    };
  } catch {
    return null;
  }
}

function App() {
  const [authReady, setAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [view, setView] = useState<View>('golden');
  const [suppliers, setSuppliers] = useState<Supplier[]>(fallbackSuppliers);
  const [activities, setActivities] = useState<Activity[]>(fallbackActivities);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics>(fallbackMetrics);
  const [connections, setConnections] = useState<ConnectionSummary[]>(fallbackConnections);
  const [recentRuns, setRecentRuns] = useState<RecentRun[]>(fallbackRecentRuns);
  const [operationsAuditEvents, setOperationsAuditEvents] = useState<any[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>(fallbackReviews);
  const [reviewAssignees, setReviewAssignees] = useState<TenantUser[]>([]);
  const [selectedId, setSelectedId] = useState('SUP-MSTR-0001');
  const [entityTabs, setEntityTabs] = useState<string[]>(['SUP-MSTR-0001']);
  const [entityTabMode, setEntityTabMode] = useState<'list' | 'record'>('list');
  const [sourceRecords, setSourceRecords] = useState<SourceRecord[]>(fallbackSources['SUP-MSTR-0001']);
  const [supplierIdentifiers, setSupplierIdentifiers] = useState<any[]>([]);
  const [profileData, setProfileData] = useState<SupplierProfileData>(fallbackProfileData(fallbackSuppliers[0]));
  const [auditEvents, setAuditEvents] = useState<any[]>([]);
  const [wizardMode, setWizardMode] = useState<'merge' | 'split' | null>(null);
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [targetId, setTargetId] = useState('dx-supplier-001');
  const [matchSignals] = useState<MatchSignal[]>(fallbackSignals);
  const [toast, setToast] = useState('');
  const [config, setConfig] = useState({ auto_merge_threshold: 0.9, review_threshold: 0.65, retention_months: 12 });
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [createSeed, setCreateSeed] = useState<GsrSeed | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [supplierLogos, setSupplierLogos] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem('gsr_logo_map');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const selected = suppliers.find((supplier) => supplier.datax_id === selectedId) || suppliers[0];
  const target = suppliers.find((supplier) => supplier.datax_id === targetId) || suppliers[2];
  const selectedReview = reviews[0];

  useEffect(() => {
    const url = new URL(window.location.href);
    const tokenFromQuery = url.searchParams.get('token');
    if (tokenFromQuery) {
      localStorage.setItem('auth_token', tokenFromQuery);
      const decodedUser = userFromJwt(tokenFromQuery);
      if (decodedUser) localStorage.setItem('user', JSON.stringify(decodedUser));
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    }
    setIsAuthenticated(!!localStorage.getItem('auth_token'));
    setAuthReady(true);
  }, []);

  useEffect(() => {
    const onExpired = () => setIsAuthenticated(false);
    window.addEventListener('auth-expired', onExpired);
    return () => window.removeEventListener('auth-expired', onExpired);
  }, []);

  useEffect(() => {
    const onAction = (event: Event) => {
      setToast(String((event as CustomEvent<string>).detail || 'Action completed.'));
    };
    window.addEventListener('supplier-master-action', onAction);
    return () => window.removeEventListener('supplier-master-action', onAction);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadInitialData();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadSupplierDetail(selectedId);
  }, [selectedId, isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('gsr_logo_map', JSON.stringify(supplierLogos));
  }, [supplierLogos]);

  useEffect(() => {
    if (!userMenuOpen) return;
    function handleOutsidePointer(event: MouseEvent) {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsidePointer);
    return () => document.removeEventListener('mousedown', handleOutsidePointer);
  }, [userMenuOpen]);

  async function loadInitialData() {
    const [supplierData, activityData, reviewData, configData] = await Promise.allSettled([
      api<{ suppliers: Supplier[] }>(`/api/golden-suppliers?tenant_id=${TENANT_ID}`),
      api<{ metrics?: DashboardMetrics; connections?: ConnectionSummary[]; recent_runs?: RecentRun[]; activities: Activity[]; audit_events?: any[]; notifications?: NotificationItem[] }>(`/api/match-merge/suppliers/activity?tenant_id=${TENANT_ID}`),
      api<{ reviews: ReviewItem[]; assignees?: TenantUser[] }>(`/api/match-merge/suppliers/review-queue?tenant_id=${TENANT_ID}`),
      api<{ config: any }>(`/api/match-merge/suppliers/config?tenant_id=${TENANT_ID}`),
    ]);

    if (supplierData.status === 'fulfilled' && supplierData.value.suppliers?.length) {
      setSuppliers(supplierData.value.suppliers);
    }
    if (activityData.status === 'fulfilled') {
      if (activityData.value.activities?.length) setActivities(activityData.value.activities);
      setDashboardMetrics(activityData.value.metrics || fallbackMetrics);
      setConnections(activityData.value.connections?.length ? activityData.value.connections : fallbackConnections);
      setRecentRuns(activityData.value.recent_runs?.length ? activityData.value.recent_runs : fallbackRecentRuns);
      setOperationsAuditEvents(activityData.value.audit_events || []);
    }
    if (reviewData.status === 'fulfilled') {
      if (reviewData.value.reviews?.length) setReviews(reviewData.value.reviews);
      setReviewAssignees(reviewData.value.assignees || []);
    }
    if (configData.status === 'fulfilled' && configData.value.config) {
      setConfig(configData.value.config);
    }
  }

  async function loadSupplierDetail(id: string) {
    const supplier = suppliers.find((item) => item.datax_id === id) || fallbackSuppliers.find((item) => item.datax_id === id) || selected;
    try {
      const data = await api<any>(`/api/golden-suppliers/${id}?tenant_id=${TENANT_ID}`);
      setSourceRecords(data.source_records || fallbackSources[id] || []);
      setSupplierIdentifiers(data.identifiers || []);
      setAuditEvents(data.audit_events || []);
      const fallback = fallbackProfileData(data.supplier || supplier);
      setProfileData({
        certifications: data.certifications?.length ? data.certifications : fallback.certifications,
        esg_scores: data.esg_scores?.length ? data.esg_scores : fallback.esg_scores,
        environmental: data.environmental?.length ? data.environmental : fallback.environmental,
        risk_assessments: data.risk_assessments?.length ? data.risk_assessments : fallback.risk_assessments,
        diversity: data.diversity?.length ? data.diversity : fallback.diversity,
        compliance: data.compliance?.length ? data.compliance : fallback.compliance,
        sanctions: data.sanctions?.length ? data.sanctions : fallback.sanctions,
        performance: data.performance?.length ? data.performance : fallback.performance,
        documents: data.documents?.length ? data.documents : fallback.documents,
        relationships: data.relationships?.length ? data.relationships : fallback.relationships,
        contacts: data.contacts?.length ? data.contacts : fallback.contacts,
        locations: data.locations?.length ? data.locations : fallback.locations,
      });
    } catch {
      setSourceRecords(fallbackSources[id] || []);
      setSupplierIdentifiers([]);
      setProfileData(fallbackProfileData(supplier));
      setAuditEvents([
        { event_type: 'manual_merge', reason: 'Steward confirmed source record mapping', changed_by_user_id: 'Jane Steward', changed_at: 1761868800 },
        { event_type: 'catalog_push', reason: 'Golden Supplier Record pushed to Catalog stub', changed_by_user_id: 'System', changed_at: 1761782400 },
      ]);
    }
  }

  async function approveReview(review: ReviewItem) {
    try {
      await api('/api/match-merge/suppliers/merge', {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: TENANT_ID,
          source_record_id: review.source_record_id,
          datax_id: review.candidate_datax_id,
          match_score: review.match_score,
          merge_method: 'manual',
          reason: 'Steward approved demo match',
        }),
      });
    } catch {
      // Fallback still updates the local demo state.
    }
    setReviews((items) => items.filter((item) => item.review_id !== review.review_id));
    setToast('Review approved and source record merged into the Golden Supplier Record.');
  }

  async function createSupplier(payload: {
    master_legal_name: string;
    tenant_supplier_code: string;
    master_tax_id?: string;
    master_duns_number?: string;
    master_website?: string;
    tenant_classification: 'strategic' | 'preferred' | 'tactical';
    tenant_status?: 'active' | 'inactive' | 'blocked';
    city?: string;
    state_province?: string;
    country?: string;
    source_xrefs?: number;
    data_quality_score?: number;
    active_certs?: number;
    esg_rating?: string;
    esg_score?: number;
    risk_level?: string;
    risk_score?: number;
    diversity_type?: string;
    diversity_status?: string;
    sanctions_status?: string;
    sanctions_provider?: string;
    doc_1_name?: string;
    doc_1_type?: string;
    doc_1_url?: string;
    doc_2_name?: string;
    doc_2_type?: string;
    doc_2_url?: string;
    doc_notes?: string;
  }) {
    await api('/api/suppliers/create', {
      method: 'POST',
      body: JSON.stringify({
        tenant_id: TENANT_ID,
        tenant_status: payload.tenant_status || 'active',
        ...payload,
      }),
    });
    await loadInitialData();
    setToast('Supplier created successfully.');
  }

  async function updateSupplier(payload: {
    tenant_supplier_id: string;
    master_legal_name: string;
    tenant_supplier_code: string;
    master_tax_id?: string;
    master_duns_number?: string;
    master_website?: string;
    tenant_classification: 'strategic' | 'preferred' | 'tactical';
    tenant_status?: 'active' | 'inactive' | 'blocked';
    city?: string;
    state_province?: string;
    country?: string;
    source_xrefs?: number;
    data_quality_score?: number;
    active_certs?: number;
    esg_rating?: string;
    esg_score?: number;
    risk_level?: string;
    risk_score?: number;
    diversity_type?: string;
    diversity_status?: string;
    sanctions_status?: string;
    sanctions_provider?: string;
    doc_1_name?: string;
    doc_1_type?: string;
    doc_1_url?: string;
    doc_2_name?: string;
    doc_2_type?: string;
    doc_2_url?: string;
    doc_notes?: string;
  }) {
    await api('/api/suppliers/update', {
      method: 'PUT',
      body: JSON.stringify({
        tenant_id: TENANT_ID,
        tenant_status: payload.tenant_status || 'active',
        ...payload,
      }),
    });
    await loadInitialData();
    setToast('Supplier updated successfully.');
  }

  function logout() {
    try {
      const googleObj = (window as any).google;
      if (googleObj?.accounts?.id?.disableAutoSelect) {
        googleObj.accounts.id.disableAutoSelect();
      }
    } catch {
      // no-op
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    window.location.href = '/';
  }

  if (!authReady) return null;
  if (!isAuthenticated) return <SignedOutScreen onLogin={() => { window.location.href = '/api/auth/google'; }} />;

  function openEntityTab(dataxId: string) {
    setSelectedId(dataxId);
    setEntityTabMode('record');
    setEntityTabs((current) => {
      const next = [dataxId, ...current.filter((id) => id !== dataxId)];
      return next.slice(0, 5);
    });
    navigate('golden');
  }

  function navigate(nextView: View) {
    if (nextView === view) return;
    setView(nextView);
  }

  function nav(label: string, id: View, icon: ReactNode) {
    const descriptions: Record<View, string> = {
      resolve: 'Single-supplier resolve and commit',
      home: 'Retired overview surface',
      golden: 'Browse, edit, and enrich tenant entities',
      review: 'Inbox workflow for enrichment records and proposals',
      governance: 'Thresholds, survivorship, source trust, and policy impact',
      playground: 'Dry-run entity resolution and enrichment request testing',
      audit: 'Immutable trace of match, merge, remap, outlier, and governance actions',
      settings: 'Manage source connections and enrichment providers',
      notifications: 'Actionable alerts and assignment follow-up',
    };
    return (
      <button className={`rail-button ${view === id ? 'active' : ''}`} onClick={() => navigate(id)} title={`${label}: ${descriptions[id]}`}>
        <span>{icon}</span>
        <strong>{label}</strong>
      </button>
    );
  }

  return (
    <div className="app-shell">
      <aside className={`rail ${railExpanded ? 'expanded' : ''}`}>
        <div className="brand-mark">
          <img src="/assets/logo/dataX.svg" alt="dataX.ai Master" className="brand-full-logo" />
          <small>Master</small>
        </div>
        <button className="rail-toggle" onClick={() => setRailExpanded((expanded) => !expanded)} aria-label={railExpanded ? 'Collapse navigation' : 'Expand navigation'}>
          <span>{railExpanded ? 'Collapse' : 'Expand'}</span>
          <b>{railExpanded ? '‹' : '›'}</b>
        </button>
        {nav('Entities', 'golden', <NavIcon path="M5 21V9l7-4 7 4v12M9 21v-7h6v7M8 10h.01M16 10h.01" />)}
        {nav('Inbox', 'review', <NavIcon path="M5 5h14M5 10h14M5 15h14M5 20h14" />)}
        {nav('Test & Sync', 'playground', <NavIcon path="M4 5h16v5H4zM4 14h7v5H4zM15 14h5v5h-5z" />)}
        {nav('Matching policy', 'governance', <NavIcon path="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />)}
        {nav('Settings', 'settings', <NavIcon path="M12 8a4 4 0 100 8 4 4 0 000-8zm8.5 4a6.5 6.5 0 01-.1 1.1l2 1.5-2 3.4-2.4-1a7.4 7.4 0 01-1.9 1.1l-.4 2.6h-4l-.4-2.6a7.4 7.4 0 01-1.9-1.1l-2.4 1-2-3.4 2-1.5A6.5 6.5 0 013.5 12c0-.4 0-.7.1-1.1l-2-1.5 2-3.4 2.4 1a7.4 7.4 0 011.9-1.1l.4-2.6h4l.4 2.6a7.4 7.4 0 011.9 1.1l2.4-1 2 3.4-2 1.5c.1.4.1.7.1 1.1z" />)}
        {nav('Audit', 'audit', <NavIcon path="M4 6h16M4 12h16M4 18h16" />)}
        <div className="rail-spacer" />
        <div className="user-menu" ref={userMenuRef}>
          <button className="avatar" onClick={() => setUserMenuOpen((open) => !open)} title="User menu">JK</button>
          {userMenuOpen && (
            <div className="user-popover">
              <strong>Jane King</strong>
              <span>Supplier steward</span>
              <button onClick={() => { setUserMenuOpen(false); navigate('governance'); }}>Resolution Policy</button>
              <button onClick={() => { setUserMenuOpen(false); navigate('settings'); }}>Settings</button>
              <button onClick={logout}>Logout</button>
            </div>
          )}
        </div>
      </aside>

      <main className="workspace">
        <section className="content">
          {toast && <div className="toast" onAnimationEnd={() => setToast('')}>{toast}</div>}
          {view === 'resolve' && (
            <ResolveLanding
              config={config}
              onOpenPlayground={() => navigate('playground')}
              onCommitted={() => {
                loadInitialData();
                setToast('Resolve action committed.');
              }}
            />
          )}
          {view === 'home' && (
            <HomeView
              metrics={dashboardMetrics}
              connections={connections}
              recentRuns={recentRuns}
              reviews={reviews}
              activities={activities}
              onReview={() => navigate('review')}
              onUploadCsv={() => setShowCsvModal(true)}
            />
          )}
          {view === 'golden' && (
            <EntitiesExperience
              suppliers={suppliers}
              selected={selected}
              sourceRecords={sourceRecords}
              supplierIdentifiers={supplierIdentifiers}
              profileData={profileData}
              auditEvents={auditEvents}
              supplierSearch={supplierSearch}
              setSupplierSearch={setSupplierSearch}
              onCreateSupplier={() => setShowCreateModal(true)}
              onEditSupplier={() => setShowEditModal(true)}
              onSelect={openEntityTab}
              onRequestEnrichment={async (ids, note) => {
                try {
                  await api('/api/entities/enrichment-requests', {
                    method: 'POST',
                    body: JSON.stringify(ids.length === 1 ? { tenant_id: TENANT_ID, mode: 'single', datax_id: ids[0], notes: note } : { tenant_id: TENANT_ID, mode: 'batch', datax_ids: ids, notes: note }),
                  });
                } catch {
                  // The UI remains useful in demo/offline mode; the backend route is added by migration 020.
                }
                setToast(ids.length === 1 ? 'Entity enrichment queued. Results will return to Inbox as proposals.' : `${ids.length} entity enrichment requests queued.`);
              }}
              onDownloadCsv={() => {
                window.location.href = `/api/entities/${encodeURIComponent(selected.datax_id)}/export.csv?tenant_id=${TENANT_ID}`;
              }}
              supplierLogos={supplierLogos}
              onSetSupplierLogo={(dataxId, logoUrl) => setSupplierLogos((prev) => ({ ...prev, [dataxId]: logoUrl }))}
              entityTabs={entityTabs}
              activeMode={entityTabMode}
              onShowList={() => setEntityTabMode('list')}
              onCloseTab={(id) => {
                setEntityTabs((current) => current.filter((tabId) => tabId !== id));
                if (selectedId === id) setEntityTabMode('list');
              }}
            />
          )}
          {view === 'review' && (
            <InboxExperience
              reviews={reviews}
              activities={activities}
              assignees={reviewAssignees}
              suppliers={suppliers}
              onOpenEntity={openEntityTab}
              onApprove={approveReview}
              onTransition={async (review, status) => {
                try {
                  await api('/api/match-merge/suppliers/review-update', {
                    method: 'POST',
                    body: JSON.stringify({
                      tenant_id: TENANT_ID,
                      source_record_id: review.source_record_id,
                      status,
                      reason: `Inbox transition: ${status}`,
                    }),
                  });
                } catch {
                  // Keep the demo UI responsive even if the network is unavailable.
                }
                setToast(`Inbox record moved to ${status.replace(/_/g, ' ')}.`);
              }}
              onCreateGsr={(seed) => {
                setCreateSeed(seed);
                setShowCreateModal(true);
              }}
              onUploadBatch={() => setShowCsvModal(true)}
            />
          )}
          {view === 'governance' && <GovernanceView config={config} setConfig={setConfig} metrics={dashboardMetrics} connections={connections} />}
          {view === 'playground' && (
            <ResolvePlayground
              config={config}
              setConfig={setConfig}
              onSaved={(nextConfig) => {
                setConfig(nextConfig);
                setToast('Resolution thresholds saved.');
              }}
            />
          )}
          {view === 'settings' && <SettingsView />}
          {view === 'audit' && <AuditView auditEvents={operationsAuditEvents} />}
          {view === 'notifications' && <NotificationsView metrics={dashboardMetrics} connections={connections} activities={activities} />}
        </section>
      </main>

      {wizardMode === 'merge' && (
        <MergeWizard
          step={wizardStep}
          setStep={setWizardStep}
          source={selectedReview}
          selected={selected}
          target={target}
          suppliers={suppliers}
          targetId={targetId}
          setTargetId={setTargetId}
          signals={matchSignals}
          onClose={() => setWizardMode(null)}
          onDone={() => {
            setWizardMode(null);
            setToast('Merge completed. Golden Supplier Record source records and audit log updated.');
          }}
        />
      )}

      {wizardMode === 'split' && (
        <SplitWizard
          step={wizardStep}
          setStep={setWizardStep}
          supplier={selected}
          sourceRecords={sourceRecords}
          onClose={() => setWizardMode(null)}
          onDone={() => {
            setWizardMode(null);
            setToast('Supplier split recorded. Source record can now be remapped.');
          }}
        />
      )}
      {showCreateModal && (
        <SupplierFormModal
          title="New supplier record"
          submitLabel="Create GSR"
          mode="create"
          seed={createSeed || undefined}
          onClose={() => { setShowCreateModal(false); setCreateSeed(null); }}
          onSubmit={async (form) => {
            try {
              await createSupplier(form);
              setShowCreateModal(false);
              setCreateSeed(null);
            } catch (error) {
              setToast(`Create failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          }}
        />
      )}
      {showEditModal && (
        <SupplierFormModal
          title="Edit supplier record"
          submitLabel="Save changes"
          mode="edit"
          seed={{ candidate_datax_id: selected.datax_id, candidate_name: selected.master_legal_name }}
          initialValues={{
            master_legal_name: selected.master_legal_name || '',
            tenant_supplier_code: selected.tenant_supplier_code || selected.datax_id || '',
            master_tax_id: selected.master_tax_id || '',
            master_duns_number: selected.master_duns_number || '',
            master_website: selected.master_website || '',
            tenant_classification: (selected.tenant_classification?.toLowerCase() as 'strategic' | 'preferred' | 'tactical') || 'preferred',
            tenant_status: (selected.tenant_status as 'active' | 'inactive' | 'blocked') || 'active',
            city: selected.city || '',
            state_province: selected.state_province || '',
            country: selected.country || 'US',
          }}
          onClose={() => setShowEditModal(false)}
          onSubmit={async (form) => {
            try {
              await updateSupplier({
                ...form,
                tenant_supplier_id: selected.tenant_supplier_id || selected.datax_id,
              });
              setShowEditModal(false);
            } catch (error) {
              setToast(`Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          }}
        />
      )}
      {showCsvModal && (
        <CsvUploadGuideModal
          onClose={() => setShowCsvModal(false)}
          onUploaded={async (summary) => {
            await loadInitialData();
            setToast(`CSV uploaded: ${summary.inserted} inserted, ${summary.updated} updated, ${summary.failed} failed.`);
          }}
        />
      )}
    </div>
  );
}

function canonicalActivityState(row: Activity) {
  const raw = String(row.queue_state || row.merge_method || row.review_status || row.record_status || '').toLowerCase();
  if (raw.includes('outlier')) return { label: 'Outlier / do not learn', tone: 'purple' };
  if (raw.includes('reject')) return { label: 'Rejected', tone: 'gray' };
  if (raw.includes('rework')) return { label: 'Rework needed', tone: 'danger' };
  if (raw.includes('fail') || raw.includes('error') || raw.includes('invalid')) return { label: 'Failed validation', tone: 'danger' };
  if (raw.includes('new') && !row.datax_id) return { label: 'New GSR candidate', tone: 'info' };
  if (raw.includes('pending')) return { label: 'Pending', tone: 'gray' };
  if (raw.includes('review')) return { label: 'Needs review', tone: 'warning' };
  if ((raw.includes('auto') || raw.includes('match')) && !row.datax_id) return { label: 'Rework needed', tone: 'danger' };
  if (raw.includes('auto') || raw.includes('match') || row.datax_id) return { label: 'Auto-linked', tone: 'success' };
  return { label: 'Pending', tone: 'gray' };
}

function normalizedTrustScore(score?: number) {
  if (!score) return 0;
  return score > 1 ? score : Math.round(score * 100);
}

function percentValue(part?: number, total?: number) {
  if (!total) return 0;
  return Math.round(((part || 0) / total) * 100);
}

function inboundRoutingProjection(metrics: DashboardMetrics, connections: ConnectionSummary[]) {
  const total = metrics.inbound_records || connections.reduce((sum, connection) => sum + (connection.total_records || 0), 0) || fallbackMetrics.inbound_records;
  const connectionAuto = connections.reduce((sum, connection) => sum + (connection.auto_records ?? connection.matched_records ?? 0), 0);
  const connectionReview = connections.reduce((sum, connection) => sum + (connection.review_records || 0), 0);
  const connectionOutliers = connections.reduce((sum, connection) => sum + (connection.rejected_records || 0), 0);
  const connectionNew = connections.reduce((sum, connection) => sum + (connection.new_records || 0), 0);
  const autoLinked = Math.min(total, metrics.auto_merged || connectionAuto || fallbackMetrics.auto_merged);
  const needsReview = Math.min(total - autoLinked, metrics.awaiting_review || connectionReview || fallbackMetrics.awaiting_review);
  const outliers = Math.min(total - autoLinked - needsReview, metrics.rejected_outliers || connectionOutliers || fallbackMetrics.rejected_outliers);
  const newCandidates = Math.min(total - autoLinked - needsReview - outliers, connectionNew || Math.round(total * 0.08));
  const pending = Math.max(total - autoLinked - needsReview - outliers - newCandidates, 0);
  return { total, autoLinked, needsReview, outliers, newCandidates, pending };
}

function matchesCurrentUser(item: { assigned_to_user_id?: string | null; assigned_to_name?: string | null; assigned_to_email?: string | null }, user: any) {
  const fallbackUser = { user_id: 'tu-001', full_name: 'Jane King', email: 'jane.king@example.com' };
  const hasUserIdentity = !!(user?.user_id || user?.id || user?.full_name || user?.name || user?.email);
  const effectiveUser = hasUserIdentity ? user : fallbackUser;
  const ids = [effectiveUser?.user_id, effectiveUser?.id].filter(Boolean).map(String);
  const names = [effectiveUser?.full_name, effectiveUser?.name].filter(Boolean).map((value) => String(value).toLowerCase());
  const emails = [effectiveUser?.email].filter(Boolean).map((value) => String(value).toLowerCase());
  return (
    (!!item.assigned_to_user_id && ids.includes(String(item.assigned_to_user_id))) ||
    (!!item.assigned_to_name && names.includes(String(item.assigned_to_name).toLowerCase())) ||
    (!!item.assigned_to_email && emails.includes(String(item.assigned_to_email).toLowerCase()))
  );
}

function HomeView({
  metrics,
  connections,
  recentRuns,
  reviews,
  activities,
  onReview,
  onUploadCsv,
}: {
  metrics: DashboardMetrics;
  connections: ConnectionSummary[];
  recentRuns: RecentRun[];
  reviews: ReviewItem[];
  activities: Activity[];
  onReview: () => void;
  onUploadCsv: () => void;
}) {
  const connectionRows = connections.length ? connections : fallbackConnections;
  const runRows = recentRuns.length ? recentRuns : fallbackRecentRuns;
  const reviewRows = reviews.length ? reviews : fallbackReviews;
  const sourceRows = activities.length ? activities : fallbackActivities;
  const reviewBySourceId = new Map(reviewRows.map((review) => [review.source_record_id, review]));
  const routing = inboundRoutingProjection(metrics, connectionRows);
  const reviewCount = routing.needsReview;
  const outlierCount = routing.outliers;
  const pendingCount = routing.pending + routing.newCandidates;
  const autoLinkedCount = routing.autoLinked;
  const total = routing.total || 1;
  const segments = [
    { label: 'Auto-linked', value: autoLinkedCount, tone: 'green' },
    { label: 'In review', value: reviewCount, tone: 'orange' },
    { label: 'Outlier / do not learn', value: outlierCount, tone: 'purple' },
    { label: 'Pending / new candidate', value: pendingCount, tone: 'gray' },
  ];
  const connectionMatchedTotal = connectionRows.reduce((sum, row) => sum + (row.matched_records || 0), 0);
  const latestRun = runRows.slice().sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0))[0];
  const topReviewRows = sourceRows
    .filter((row) => sourceState(row).label === 'review')
    .sort((a, b) => (b.match_score || 0) - (a.match_score || 0))
    .slice(0, 5)
    .map((row) => {
      const review = reviewBySourceId.get(row.source_record_id);
      return {
        key: row.source_record_id,
        score: row.match_score || review?.match_score || 0,
        sourceSupplierName: row.source_supplier_name || review?.source_supplier_name || '-',
        candidateName: row.master_legal_name || review?.candidate_name || 'Needs steward decision',
        sourceName: row.source_name || review?.source_name || '-',
        summary: row.match_summary || review?.match_summary || (row.scoring_status === 'pending_scoring' ? 'Pending scoring' : ''),
      };
    });
  const user = currentUser();
  const assignedRows = reviewRows
    .filter((row) => matchesCurrentUser(row, user))
    .sort((a, b) => (b.match_score || 0) - (a.match_score || 0))
    .slice(0, 5);

  return (
    <>
      <div className="page-heading operations-heading clarity-heading">
        <div>
          <div className="breadcrumb">Home › Inbound Resolution</div>
          <h1>Inbound Resolution</h1>
          <p>Track source records through the resolution pipeline, from ingest to the golden master.</p>
        </div>
        <button className="primary-button severity-primary" onClick={onUploadCsv}>Upload batch</button>
      </div>

      <section className="panel pipeline-status-card">
        <div className="pipeline-card-top">
          <div>
            <span className="section-label">Pipeline status</span>
            <div className="pipeline-count">
              <strong>{total.toLocaleString()}</strong>
              <span>inbound source records across {connectionRows.length} connections</span>
            </div>
          </div>
          <div className="last-update">
            <span>Last inbound update</span>
            <strong>{metrics.data_freshness_at ? formatDate(metrics.data_freshness_at) : '-'}</strong>
          </div>
        </div>

        <div className="pipeline-progress" aria-label="Inbound resolution status">
          {segments.map((segment) => (
            <span
              key={segment.label}
              className={`pipeline-progress-segment ${segment.tone}`}
              style={{ width: `${Math.max((segment.value / total) * 100, 2)}%` }}
              title={`${segment.label}: ${segment.value.toLocaleString()}`}
            />
          ))}
        </div>

        <div className="pipeline-legend">
          {segments.map((segment) => (
            <span key={segment.label}>
              <i className={segment.tone} />
              {segment.label}
              <strong>{segment.value.toLocaleString()}</strong>
              <em>({((segment.value / total) * 100).toFixed(1)}%)</em>
            </span>
          ))}
        </div>

        <div className="pipeline-summary-tiles">
          <div className="summary-tile green">
            <strong>{autoLinkedCount.toLocaleString()}</strong>
            <span>Auto-linked</span>
            <em>no human review</em>
          </div>
          <div className="summary-tile orange">
            <strong>{reviewCount.toLocaleString()}</strong>
            <span>Awaiting review</span>
            <em>open in queue</em>
          </div>
          <div className="summary-tile purple">
            <strong>{outlierCount.toLocaleString()}</strong>
            <span>Outlier / excluded</span>
            <em>excluded from learning</em>
          </div>
          <div className="summary-tile blue">
            <strong>{metrics.golden_suppliers.toLocaleString()}</strong>
            <span>Golden records</span>
            <em>in the golden master</em>
          </div>
        </div>
      </section>

      <div className="home-clarity-grid">
        <section className="panel connections-overview">
          <div className="table-card-header">
            <h2>Connections</h2>
            <span>{connectionRows.length} active</span>
          </div>
          <table className="data-table connections-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Records</th>
                <th>Review</th>
                <th>Matched</th>
                <th>Last run</th>
              </tr>
            </thead>
            <tbody>
              {connectionRows.map((connection) => (
                <tr key={connection.source_system_id}>
                  <td>
                    <span className={`health-dot ${connection.status === 'watch' ? 'orange' : 'green'}`} />
                    <strong>{connection.source_name}</strong>
                    <em>{connection.connection_type}</em>
                  </td>
                  <td>{connection.total_records.toLocaleString()}</td>
                  <td className="numeric-warning">{connection.review_records.toLocaleString()}</td>
                  <td className="numeric-success">{connection.matched_records.toLocaleString()}</td>
                  <td>{connection.last_activity_at ? formatDate(connection.last_activity_at) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="panel top-review-card">
          <div className="table-card-header">
            <h2>Needs your review</h2>
            <button className="ghost-button review-all-link" onClick={onReview}>All {reviewCount.toLocaleString()} →</button>
          </div>
          <div className="top-review-list">
            {topReviewRows.map((row) => (
              <article className="top-review-row" key={row.key}>
                <span className="score-chip">{fmtScore(row.score)}</span>
                <div>
                  <strong>{row.sourceSupplierName}</strong>
                  <p>→ {row.candidateName} · {row.sourceName}</p>
                  {row.summary && <small>{row.summary}</small>}
                </div>
                <button className="primary-button severity-success" onClick={onReview}>Approve</button>
              </article>
            ))}
          </div>
        </section>
      </div>

      <section className="panel operational-context">
        <div>
          <span className="section-label">Operational context</span>
          <strong>{connectionMatchedTotal.toLocaleString()} matched records across active feeds</strong>
          <p>Latest run: {latestRun ? `${latestRun.run_label} - ${latestRun.total_records.toLocaleString()} records` : 'no recent run'}. Notifications surface as contextual alerts where the work happens.</p>
        </div>
      </section>

      <section className="panel assigned-work-card">
        <div className="table-card-header">
          <h2>Assigned to you</h2>
          <button className="ghost-button review-all-link" onClick={onReview}>{assignedRows.length ? `Open ${assignedRows.length}` : 'Open queue'} →</button>
        </div>
        <div className="assigned-work-list">
          {assignedRows.length ? assignedRows.map((row) => (
            <article className="assigned-work-row" key={row.review_id}>
              <span className="score-chip">{fmtScore(row.match_score)}</span>
              <div>
                <strong>{row.source_supplier_name}</strong>
                <p>{row.source_name} · candidate {row.candidate_name}</p>
                <small>{row.match_summary || (row.scoring_status === 'pending_scoring' ? 'Pending scoring' : '')}</small>
              </div>
              <button className="secondary-button row-action" onClick={onReview}>Review</button>
            </article>
          )) : (
            <div className="assigned-empty">No records are currently assigned to you.</div>
          )}
        </div>
      </section>
    </>
  );
}

function sourceState(row: Activity) {
  const state = canonicalActivityState(row).label;
  if (state === 'Auto-linked') return { label: 'matched', tone: 'success' };
  if (state === 'Needs review') return { label: 'review', tone: 'warning' };
  if (state === 'New GSR candidate') return { label: 'new', tone: 'info' };
  if (state === 'Outlier / do not learn') return { label: 'outlier', tone: 'purple' };
  if (state === 'Rework needed') return { label: 'rework', tone: 'danger' };
  if (state === 'Failed validation') return { label: 'failed', tone: 'danger' };
  if (state === 'Rejected') return { label: 'rejected', tone: 'gray' };
  return { label: 'pending', tone: 'gray' };
}

type EntityRecordTab = 'overview' | 'identifiers' | 'codes' | 'related' | 'matched' | 'contacts' | 'agreements' | 'documents' | 'notes' | 'lineage';

const entityTypes = ['Supplier', 'Vendor', 'Distributor', 'Manufacturer', 'Brand', 'Gov/Public', 'Other'];
const entitySources = ['Manual', 'ERP', 'D&B', 'Registry', 'Connector'];

function entityTypeFor(supplier: Supplier) {
  const hash = Array.from(supplier.datax_id || supplier.master_legal_name || '').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return entityTypes[hash % entityTypes.length];
}

function primarySourceFor(supplier: Supplier) {
  const hash = Array.from(supplier.master_legal_name || supplier.datax_id || '').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return entitySources[hash % entitySources.length];
}

function tierFor(supplier: Supplier) {
  const raw = String(supplier.tenant_classification || '').toLowerCase();
  if (raw.includes('strategic')) return 'T1';
  if (raw.includes('preferred')) return 'T2';
  if (raw.includes('tactical')) return 'T3';
  return 'T2';
}

function hasPendingUpdate(supplier: Supplier) {
  return (supplier.data_quality_score || 0) < 80 || supplier.datax_id.endsWith('1');
}

function statusTone(status?: string) {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('block') || normalized.includes('cancel') || normalized.includes('insufficient')) return 'danger';
  if (normalized.includes('inactive') || normalized.includes('rework') || normalized.includes('review')) return 'warning';
  if (normalized.includes('published') || normalized.includes('approved') || normalized.includes('active')) return 'success';
  if (normalized.includes('queue') || normalized.includes('progress') || normalized.includes('processed')) return 'info';
  return 'gray';
}

function EntitiesExperience({
  suppliers,
  selected,
  sourceRecords,
  supplierIdentifiers,
  profileData,
  auditEvents,
  supplierSearch,
  setSupplierSearch,
  onCreateSupplier,
  onEditSupplier,
  onSelect,
  onRequestEnrichment,
  onDownloadCsv,
  supplierLogos,
  onSetSupplierLogo,
  entityTabs,
  activeMode,
  onShowList,
  onCloseTab,
}: {
  suppliers: Supplier[];
  selected: Supplier;
  sourceRecords: SourceRecord[];
  supplierIdentifiers: any[];
  profileData: SupplierProfileData;
  auditEvents: any[];
  supplierSearch: string;
  setSupplierSearch: (value: string) => void;
  onCreateSupplier: () => void;
  onEditSupplier: () => void;
  onSelect: (id: string) => void;
  onRequestEnrichment: (ids: string[], note?: string) => void | Promise<void>;
  onDownloadCsv: () => void;
  supplierLogos: Record<string, string>;
  onSetSupplierLogo: (dataxId: string, logoUrl: string) => void;
  entityTabs: string[];
  activeMode: 'list' | 'record';
  onShowList: () => void;
  onCloseTab: (id: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [linkedFilter, setLinkedFilter] = useState('all');
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [recordTab, setRecordTab] = useState<EntityRecordTab>('overview');
  const [showEnrichment, setShowEnrichment] = useState(false);
  const [enrichmentNote, setEnrichmentNote] = useState('');
  const openTabs = entityTabs.map((id) => suppliers.find((supplier) => supplier.datax_id === id)).filter(Boolean) as Supplier[];
  const filtered = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    return suppliers.filter((supplier) => {
      const type = entityTypeFor(supplier);
      const source = primarySourceFor(supplier);
      const tier = tierFor(supplier);
      const haystack = [
        supplier.master_legal_name,
        supplier.tenant_supplier_code,
        supplier.master_duns_number,
        supplier.master_tax_id,
        supplier.master_website,
        getGsrScenario(supplier).names.map((name: any) => name.value).join(' '),
        'BR-HOSP GL-2410',
      ].join(' ').toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (statusFilter !== 'all' && String(supplier.tenant_status || '').toLowerCase() !== statusFilter) return false;
      if (typeFilter !== 'all' && type !== typeFilter) return false;
      if (sourceFilter !== 'all' && source !== sourceFilter) return false;
      if (tierFilter !== 'all' && tier !== tierFilter) return false;
      if (pendingOnly && !hasPendingUpdate(supplier)) return false;
      if (linkedFilter === 'linked' && !supplier.datax_id) return false;
      if (linkedFilter === 'tenant-only' && supplier.datax_id) return false;
      return true;
    });
  }, [linkedFilter, pendingOnly, sourceFilter, statusFilter, supplierSearch, suppliers, tierFilter, typeFilter]);
  const checkedIds = Object.entries(selectedRows).filter(([, checked]) => checked).map(([id]) => id);
  const allVisibleChecked = !!filtered.length && filtered.every((supplier) => selectedRows[supplier.datax_id]);

  function submitEnrichment(ids: string[]) {
    onRequestEnrichment(ids, enrichmentNote);
    setShowEnrichment(false);
    setEnrichmentNote('');
  }

  return (
    <div className="entities-experience">
      <div className="page-heading compact-heading entity-heading">
        <div>
          <div className="breadcrumb">Entities</div>
          <h1>Entities</h1>
          <p>Default registry for suppliers, vendors, distributors, manufacturers, brands, and public bodies.</p>
        </div>
        <div className="button-row">
          <button className="primary-button severity-primary" onClick={onCreateSupplier}>Add entity</button>
        </div>
      </div>

      <div className="entity-tab-strip" role="tablist" aria-label="Open entity tabs">
        <button className={activeMode === 'list' ? 'active pinned' : 'pinned'} onClick={onShowList} role="tab" aria-selected={activeMode === 'list'}>All entities</button>
        {openTabs.map((supplier) => (
          <button key={supplier.datax_id} className={activeMode === 'record' && selected.datax_id === supplier.datax_id ? 'active' : ''} onClick={() => onSelect(supplier.datax_id)} role="tab" aria-selected={activeMode === 'record' && selected.datax_id === supplier.datax_id}>
            {supplier.master_legal_name}
            <span onClick={(event) => { event.stopPropagation(); onCloseTab(supplier.datax_id); }} aria-label={`Close ${supplier.master_legal_name}`}>×</span>
          </button>
        ))}
        <button className="ghost-tab" onClick={onShowList}>+</button>
      </div>

      {activeMode === 'list' ? (
        <section className="panel entities-list-panel">
          <div className="inbox-attention-bar">
            <strong>{filtered.filter(hasPendingUpdate).length} pending updates</strong>
            <span>Enrichment is queued asynchronously and returns proposals to Inbox. Nothing auto-mutates an entity.</span>
          </div>
          <div className="entity-toolbar">
            <div className="search-input">
              <span>⌕</span>
              <input value={supplierSearch} onChange={(event) => setSupplierSearch(event.target.value)} placeholder="Search name, client id, DUNS, tax, alias, brand or GL code" />
            </div>
            <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}><option value="all">Entity type</option>{entityTypes.map((type) => <option key={type}>{type}</option>)}</select>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">Status</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="blocked">Blocked</option></select>
            <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}><option value="all">Source</option>{entitySources.map((source) => <option key={source}>{source}</option>)}</select>
            <select value={tierFilter} onChange={(event) => setTierFilter(event.target.value)}><option value="all">Tier</option><option>T1</option><option>T2</option><option>T3</option></select>
            <select value={linkedFilter} onChange={(event) => setLinkedFilter(event.target.value)}><option value="all">Master link</option><option value="linked">Linked</option><option value="tenant-only">Tenant-only</option></select>
            <label className="toggle-filter"><input type="checkbox" checked={pendingOnly} onChange={(event) => setPendingOnly(event.target.checked)} /> Pending update</label>
          </div>
          {!!checkedIds.length && (
            <div className="bulk-action-bar active">
              <strong>{checkedIds.length} selected</strong>
              <button className="secondary-button severity-secondary" onClick={() => downloadEntitiesCsv(filtered.filter((supplier) => selectedRows[supplier.datax_id]))}>Export CSV</button>
            </div>
          )}
          <div className="review-table-wrap">
            <table className="data-table entities-table">
              <thead>
                <tr>
                  <th><input type="checkbox" checked={allVisibleChecked} onChange={(event) => {
                    const next = { ...selectedRows };
                    filtered.forEach((supplier) => { next[supplier.datax_id] = event.target.checked; });
                    setSelectedRows(next);
                  }} aria-label="Select all visible entities" /></th>
                  <th aria-sort="ascending">Entity name</th>
                  <th>Entity type</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Tier</th>
                  <th>DQ</th>
                  <th>Last updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((supplier) => {
                  const dq = Math.max(0, Math.min(100, supplier.data_quality_score || 0));
                  return (
                    <tr key={supplier.datax_id} className="clickable-row" onClick={() => onSelect(supplier.datax_id)}>
                      <td onClick={(event) => event.stopPropagation()}><input type="checkbox" checked={!!selectedRows[supplier.datax_id]} onChange={(event) => setSelectedRows((current) => ({ ...current, [supplier.datax_id]: event.target.checked }))} aria-label={`Select ${supplier.master_legal_name}`} /></td>
                      <td>
                        <div className="table-supplier-cell">
                          <Logo supplier={{ ...supplier, logo_url: supplierLogos[supplier.datax_id] || supplier.logo_url }} />
                          <div>
                            <strong>{supplier.master_legal_name}{hasPendingUpdate(supplier) && <i className="pending-dot" title="Pending dataX update" />}</strong>
                            <span>{supplier.tenant_supplier_code || 'Client id missing'}</span>
                          </div>
                        </div>
                      </td>
                      <td>{entityTypeFor(supplier)}</td>
                      <td><span className={`pill ${statusTone(supplier.tenant_status)}`}>{supplier.tenant_status || 'active'}</span></td>
                      <td>{primarySourceFor(supplier)}</td>
                      <td><span className="pill gray">{tierFor(supplier)}</span></td>
                      <td><div className={`dq-mini ${dq >= 80 ? 'good' : dq >= 60 ? 'mid' : 'low'}`}><i style={{ width: `${Math.max(6, dq)}%` }} /></div><span className="dq-number">{dq}%</span></td>
                      <td>{formatDate(supplier.updated_at || supplier.last_catalog_sync)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <EntityRecord
          selected={selected}
          sourceRecords={sourceRecords}
          supplierIdentifiers={supplierIdentifiers}
          profileData={profileData}
          auditEvents={auditEvents}
          recordTab={recordTab}
          setRecordTab={setRecordTab}
          onEditSupplier={onEditSupplier}
          onOpenEntity={onSelect}
          onRequestEnrichment={() => setShowEnrichment(true)}
          onDownloadCsv={onDownloadCsv}
          logoUrl={supplierLogos[selected.datax_id] || selected.logo_url || ''}
          onSetLogo={(logoUrl) => onSetSupplierLogo(selected.datax_id, logoUrl)}
        />
      )}

      {showEnrichment && (
        <div className="drawer-backdrop" role="dialog" aria-modal="true" aria-label="Request enrichment" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowEnrichment(false); }}>
          <div className="enrichment-modal panel" style={{ width: 520, margin: 0, padding: 0, overflow: 'hidden', alignSelf: 'center' }}>
            <div className="table-card-header" style={{ padding: '18px 22px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
                <div style={{ width: 40, height: 40, borderRadius: 11, background: '#0c4af3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <EnrichGlyph size={20} color="#fff" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0 }}>Request enrichment</h2>
                  <p style={{ margin: '3px 0 0', color: '#667085', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.master_legal_name}</p>
                </div>
              </div>
              <button className="icon-button" onClick={() => setShowEnrichment(false)}>×</button>
            </div>
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#eef2fe', border: '1px solid #bfdbfe', borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ width: 18, height: 18, borderRadius: 5, background: '#dbeafe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1d4ed8', display: 'block' }} />
                </span>
                <div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 12, color: '#1e3a8a', marginBottom: 3 }}>Scope: <span style={{ background: 'rgba(12,74,243,0.12)', borderRadius: 4, padding: '1px 6px' }}>org-level</span></div>
                  <div style={{ fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 12, color: '#1d4ed8', lineHeight: 1.5 }}>This request covers the whole supplier record across your organisation — not just the current view.</div>
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 12, color: '#707A8F', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>What dataX will do</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    'Propose new or updated identifiers (DUNS, LEI, Tax ID)',
                    'Check for duplicate or near-duplicate master records',
                    'Fill firmographic gaps (NAICS, SIC, employee count, address)',
                    'Verify and cross-reference against official registry sources',
                  ].map((item) => (
                    <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ width: 18, height: 18, borderRadius: 5, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#0c4af3', display: 'block' }} />
                      </span>
                      <span style={{ fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 13, color: '#475467', lineHeight: 1.45 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontFamily: "'Inter',sans-serif", fontWeight: 600, fontSize: 12, color: '#707A8F', display: 'block', marginBottom: 6 }}>Note to dataX team <span style={{ fontWeight: 400, color: '#98a2b3' }}>(optional)</span></label>
                <textarea value={enrichmentNote} onChange={(event) => setEnrichmentNote(event.target.value)} placeholder="Context, priorities, or fields to focus on…" style={{ width: '100%', minHeight: 92, border: '1px solid #d4d7dd', borderRadius: 8, padding: '10px 12px', fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 13, color: '#16181d', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e4e9', borderRadius: 8, padding: '10px 13px', fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 12, color: '#667085', lineHeight: 1.6 }}>
                <strong style={{ color: '#344054' }}>Async & proposal-only.</strong> Results return to your <strong style={{ color: '#344054' }}>Inbox</strong> as proposals — nothing changes on this record until you review and apply them.
              </div>
            </div>
            <div className="wizard-actions" style={{ padding: '13px 22px 16px', borderTop: '1px solid #e4e7ec', marginTop: 0 }}>
              <button className="secondary-button" onClick={() => setShowEnrichment(false)}>Cancel</button>
              <button className="primary-button severity-info" onClick={() => submitEnrichment(checkedIds.length ? checkedIds : [selected.datax_id])} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <EnrichGlyph size={14} color="#fff" />
                Submit to queue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EntityRecord({
  selected,
  sourceRecords,
  supplierIdentifiers,
  profileData,
  auditEvents,
  recordTab,
  setRecordTab,
  onEditSupplier,
  onOpenEntity,
  onRequestEnrichment,
  onDownloadCsv,
  logoUrl,
  onSetLogo,
}: {
  selected: Supplier;
  sourceRecords: SourceRecord[];
  supplierIdentifiers: any[];
  profileData: SupplierProfileData;
  auditEvents: any[];
  recordTab: EntityRecordTab;
  setRecordTab: (tab: EntityRecordTab) => void;
  onEditSupplier: () => void;
  onOpenEntity: (id: string) => void;
  onRequestEnrichment: () => void;
  onDownloadCsv: () => void;
  logoUrl: string;
  onSetLogo: (logoUrl: string) => void;
}) {
  const scenario = useMemo(() => getGsrScenario(selected), [selected]);
  const customAttributes = [
    ['ERP org code', 'ORG-7781'],
    ['Payment group', 'Hospitality AP'],
    ['Risk owner', 'Procurement ops'],
  ];
  const tabs: [EntityRecordTab, string][] = [
    ['overview', 'Overview'],
    ['identifiers', 'Identifiers'],
    ['codes', 'Codes & attributes'],
    ['related', 'Related entities'],
    ['matched', 'Matched records'],
    ['contacts', 'Contacts'],
    ['agreements', 'Agreements'],
    ['documents', 'Documents'],
    ['notes', 'Notes'],
    ['lineage', 'Lineage'],
  ];
  const operatingOut = hasPendingUpdate(selected) && (selected.data_quality_score || 0) < 78;
  return (
    <section className="panel entity-record">
      <div className="entity-record-header">
        <div className="summary-identity">
          <LogoSlot supplier={selected} logoUrl={logoUrl} onUpload={onSetLogo} />
          <div>
            <h2>{selected.master_legal_name}</h2>
            <div className="entity-type-subtitle">{entityTypeFor(selected)}</div>
            <div className="identity-tags">
              <span className="client-id-chip">{selected.tenant_supplier_code || 'Client master id missing'}</span>
              {selected.datax_id && <button className="datax-chip" title="Copy dataX record id" onClick={() => {
                navigator.clipboard?.writeText(selected.datax_id).catch(() => undefined);
                announceAction(`Copied ${selected.datax_id} to clipboard.`);
              }}>{hasPendingUpdate(selected) && <i />}dataX {selected.datax_id}</button>}
            </div>
          </div>
        </div>
        <div className="summary-actions">
          <button className="secondary-button" onClick={onEditSupplier}>Edit</button>
          <button className="enrichment-action-button" onClick={onRequestEnrichment}><EnrichGlyph size={14} color="#1d4ed8" /><span>Request enrichment</span></button>
          <button className="secondary-button" onClick={onDownloadCsv}>Download CSV</button>
        </div>
      </div>
      <div className="record-status-row">
        <span>Record status <strong className={`pill ${statusTone(selected.tenant_status)}`}>{selected.tenant_status || 'active'}</strong></span>
        <span>Operating status <strong className={`pill ${operatingOut ? 'danger' : 'info'}`}>{operatingOut ? 'Out of Business (9075)' : 'Active (9074)'}</strong></span>
        {operatingOut && <span className="pill danger">Bankruptcy flag</span>}
        <span>Tier <strong>{tierFor(selected)}</strong></span>
        <span>Completeness <strong>{selected.data_quality_score || 0}%</strong></span>
      </div>
      <div className="tabs profile-tabs" role="tablist">
        {tabs.map(([tab, label]) => <button key={tab} className={recordTab === tab ? 'active' : ''} onClick={() => setRecordTab(tab)} role="tab" aria-selected={recordTab === tab}>{label}</button>)}
      </div>
      <div className="entity-record-body">
        {recordTab === 'overview' && (
          <section className="profile-section">
            <div className="profile-card-grid">
              <article className="info-card identity-card"><span className="card-kicker">Jurisdiction</span><strong>{scenario.overview.registry.jurisdiction}</strong></article>
              <article className="info-card identity-card"><span className="card-kicker">Entity type</span><strong>{entityTypeFor(selected)}</strong></article>
              <article className="info-card identity-card"><span className="card-kicker">NAICS / SIC</span><strong>722511 / 5812</strong></article>
              <article className="info-card identity-card"><span className="card-kicker">Source records</span><strong>{sourceRecords.length || selected.xref_count || 0}</strong></article>
            </div>
            <div className="profile-card-grid">
              <div className="info-card identity-card">
                <span className="card-kicker">Identifiers</span>
                <dl>
                  {(scenario.overview.identifiers || []).map((identifier: any) => (
                    <div key={identifier.label}>
                      <dt>{identifier.label}</dt>
                      <dd>{identifier.value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="info-card identity-card"><span className="card-kicker">Source & freshness</span><p>Primary source: {primarySourceFor(selected)} · last updated {formatDate(selected.updated_at || selected.last_catalog_sync)}</p></div>
            </div>
          </section>
        )}
        {recordTab === 'identifiers' && (
          <section className="profile-section">
            <table className="data-table compact-table"><thead><tr><th>Identifier</th><th>Value</th><th>Source</th><th>As of</th></tr></thead><tbody>
              {[
                ['Client master id', selected.tenant_supplier_code || '-', 'Tenant ERP', 'Current'],
                ['DUNS', selected.master_duns_number || '- missing', 'D&B', 'May 2026'],
                ['Tax / EIN', selected.master_tax_id || '- missing', 'ERP', 'May 2026'],
                ['LEI', '5493001KJTIIGC8Y1R12', 'Registry', 'Apr 2026'],
                ['NAICS', '722511', 'Registry', 'Apr 2026'],
                ['SIC', '5812', 'Registry', 'Apr 2026'],
                ...supplierIdentifiers.slice(0, 3).map((row: any) => [String(row.id_type || 'Identifier'), String(row.id_value || '-'), String(row.source || 'Source'), row.as_of_date ? formatDate(row.as_of_date) : 'Current']),
              ].map(([label, value, source, asOf]) => <tr key={`${label}-${value}`}><td>{label}</td><td>{value}</td><td>{source}</td><td>{asOf}</td></tr>)}
            </tbody></table>
          </section>
        )}
        {recordTab === 'codes' && (
          <section className="profile-section">
            <EntityCodeCard title="Brand codes" tone="brand" codes={['BR-HOSP Hospitality', 'BR-F&B Food & beverage']} />
            <EntityCodeCard title="GL / account codes" tone="gl" codes={['GL-2410 Ingredients', 'GL-8040 Logistics']} />
            <div className="info-card identity-card"><span className="card-kicker">Custom attributes</span><strong>{customAttributes.length} / 20</strong>{customAttributes.map(([name, value]) => <p key={name}><b>{name}</b> → {value}</p>)}<p className="settings-managed-note">Attribute names are governed in Settings so stewards apply the same schema across every entity.</p></div>
          </section>
        )}
        {recordTab === 'related' && <RelatedEntitiesTab selected={selected} suppliers={fallbackSuppliers.concat(selected).filter((item, index, arr) => arr.findIndex((s) => s.datax_id === item.datax_id) === index).slice(0, 5)} onOpenEntity={onOpenEntity} />}
        {recordTab === 'matched' && <MatchedRecordsTab records={sourceRecords} onOpenEntity={onOpenEntity} />}
        {recordTab === 'contacts' && <EntitySimpleList title="Contacts" rows={(profileData.contacts.length ? profileData.contacts : [{ contact_id: 'primary', first_name: scenario.overview.contactName, email: scenario.overview.contactEmail, phone: scenario.overview.contactPhone }]).map((contact: any) => `${contact.first_name || ''} ${contact.last_name || ''} · ${contact.email || 'no email'} · ${contact.phone || 'no phone'}`)} />}
        {recordTab === 'agreements' && <GsrAgreementsTab scenario={scenario} />}
        {recordTab === 'documents' && <EntitySimpleList title="Documents" rows={(profileData.documents.length ? profileData.documents : fallbackProfileData(selected).documents).map((doc: any) => `${doc.document_name || doc.name} · ${doc.document_type || doc.doc_type || 'other'} · ${doc.file_size || doc.size_bytes || 0} bytes`)} action="Drag files here or upload" onAction={() => announceAction('Document upload picker opened for this entity.')} />}
        {recordTab === 'notes' && <EntitySimpleList title="Notes" rows={['Jane King · Watching operating-status update before next sync.', 'System · Enrichment request notes and comments stay tenant-scoped.']} action="Add note" onAction={() => announceAction('Note composer opened for this entity.')} />}
        {recordTab === 'lineage' && <GsrHistoryTab auditEvents={auditEvents} corporateEvents={[]} filings={scenario.filings} />}
      </div>
    </section>
  );
}

function EntityCodeCard({ title, tone, codes }: { title: string; tone: 'brand' | 'gl'; codes: string[] }) {
  return <div className="info-card identity-card"><span className="card-kicker">{title}</span><div className="code-chip-row">{codes.map((code) => <span key={code} className={`code-chip ${tone}`}>{code}</span>)}</div><p className="settings-managed-note">Managed from Settings and resolved from inbox documents during review.</p></div>;
}

function RelatedEntitiesTab({ selected, suppliers, onOpenEntity }: { selected: Supplier; suppliers: Supplier[]; onOpenEntity: (id: string) => void }) {
  const rows = suppliers.filter((supplier) => supplier.datax_id !== selected.datax_id).slice(0, 4);
  return <section className="profile-section"><table className="data-table compact-table"><thead><tr><th>Relationship</th><th>Entity</th><th>Ownership</th><th>Verification</th></tr></thead><tbody>{rows.map((supplier, index) => <tr key={supplier.datax_id}><td>{['Parent', 'Subsidiary', 'Affiliate', 'Sister concern'][index]}</td><td><button className="link-button" onClick={() => onOpenEntity(supplier.datax_id)}>{supplier.master_legal_name}</button></td><td>{index === 0 ? '100%' : index === 1 ? '62%' : '-'}</td><td><span className="pill info">Verified</span></td></tr>)}</tbody></table></section>;
}

function MatchedRecordsTab({ records, onOpenEntity }: { records: SourceRecord[]; onOpenEntity: (id: string) => void }) {
  return <section className="profile-section"><table className="data-table compact-table"><thead><tr><th>Inbox document</th><th>Resolved entity</th><th>GL code</th><th>Confidence</th><th>Evidence</th></tr></thead><tbody>{(records.length ? records : fallbackSources['SUP-MSTR-0001']).map((record) => <tr key={record.source_record_id}><td><button className="link-button" onClick={() => onOpenEntity('SUP-MSTR-0001')}>{record.document_id || record.source_record_id}</button><span className="subtle-row-note">{record.document_type || 'Source record'} · {record.source_name}</span></td><td>{record.resolved_entity || record.source_supplier_name}<span className="subtle-row-note">{record.source_supplier_name}</span></td><td>{record.gl_code || 'Pending review'}</td><td>{Math.round((record.match_score || 0.7) * 100)}%<span className="subtle-row-note">{record.merge_method || 'manual'}</span></td><td>{record.evidence || record.match_summary || 'Matched from normalized supplier evidence.'}</td></tr>)}</tbody></table></section>;
}

function EntitySimpleList({ title, rows, action, onAction }: { title: string; rows: string[]; action?: string; onAction?: () => void }) {
  return <section className="profile-section"><div className="info-card identity-card"><span className="card-kicker">{title}</span>{rows.map((row) => <p key={row}>{row}</p>)}{action && <button className="secondary-button" onClick={onAction || (() => announceAction(`${title} action opened.`))}>{action}</button>}</div></section>;
}

function downloadEntitiesCsv(rows: Supplier[]) {
  const csv = ['entity_name,client_master_id,entity_type,status,source,tier,dq,last_updated', ...rows.map((supplier) => [
    supplier.master_legal_name,
    supplier.tenant_supplier_code || '',
    entityTypeFor(supplier),
    supplier.tenant_status || 'active',
    primarySourceFor(supplier),
    tierFor(supplier),
    supplier.data_quality_score || 0,
    formatDate(supplier.updated_at || supplier.last_catalog_sync),
  ].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'entities.csv';
  link.click();
  URL.revokeObjectURL(url);
}

const inboxTabs = ['All', 'In Queue', 'In Progress', 'Needs Review', 'Processed', 'Approved', 'Published', 'Rework', 'Insufficient data', 'Duplicate', 'Cancelled', 'Other', 'Batches'];

function InboxExperience({
  reviews,
  activities,
  assignees,
  suppliers,
  onOpenEntity,
  onApprove,
  onTransition,
  onCreateGsr,
  onUploadBatch,
}: {
  reviews: ReviewItem[];
  activities: Activity[];
  assignees: TenantUser[];
  suppliers: Supplier[];
  onOpenEntity: (dataxId: string) => void;
  onApprove: (review: ReviewItem) => void;
  onTransition: (review: ReviewItem, status: string) => void | Promise<void>;
  onCreateGsr: (seed: GsrSeed) => void;
  onUploadBatch: () => void;
}) {
  const [activeTab, setActiveTab] = useState('All');
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [detail, setDetail] = useState<InboxRow | null>(null);
  const [comments, setComments] = useState<Record<string, string[]>>({});
  const [tags, setTags] = useState<Record<string, string[]>>({});
  const rows = useMemo(() => buildInboxRows(activities.length ? activities : fallbackActivities, reviews, suppliers), [activities, reviews, suppliers]);
  const batches = useMemo(() => Array.from(new Set(rows.map((row) => row.batchId))).map((batchId) => ({ batchId, name: `Batch ${batchId}`, rows: rows.filter((row) => row.batchId === batchId) })), [rows]);
  const filtered = rows.filter((row) => {
    const rowTags = tags[row.id] || row.tags;
    const matchesTab = activeTab === 'All' || activeTab === 'Batches' || row.statusLabel === activeTab;
    const hay = `${row.id} ${row.batchId} ${row.name} ${row.summary} ${row.source} ${rowTags.join(' ')}`.toLowerCase();
    if (!matchesTab) return false;
    if (query && !hay.includes(query.toLowerCase())) return false;
    if (sourceFilter !== 'all' && row.source !== sourceFilter) return false;
    if (tagFilter !== 'all' && !rowTags.includes(tagFilter)) return false;
    if (assigneeFilter !== 'all' && row.assigneeId !== assigneeFilter) return false;
    if (batchFilter !== 'all' && row.batchId !== batchFilter) return false;
    return true;
  });
  const allTags = Array.from(new Set(rows.flatMap((row) => [...row.tags, ...(tags[row.id] || [])])));
  const sources = Array.from(new Set(rows.map((row) => row.source)));
  const tabCounts = inboxTabs.reduce<Record<string, number>>((acc, tab) => ({ ...acc, [tab]: tab === 'All' ? rows.length : tab === 'Batches' ? batches.length : rows.filter((row) => row.statusLabel === tab).length }), {});

  function transition(row: InboxRow, status: string) {
    onTransition(row.review, status);
    setDetail(null);
  }

  return (
    <>
      <div className="page-heading compact-heading entity-heading">
        <div>
          <div className="breadcrumb">Inbox</div>
          <h1>Inbox</h1>
          <p>Records moving through enrichment: review, approve, publish. Survivorship and audit run on apply.</p>
        </div>
        <button className="primary-button severity-primary" onClick={onUploadBatch}>Upload batch</button>
      </div>
      <section className="panel review-board inbox-board">
        <div className="inbox-attention-bar"><strong>{rows.filter((row) => row.statusLabel === 'Needs Review').length} need review</strong><span>No unmatched banner here; upload batches and single requests enter the same workflow.</span></div>
        <div className="status-chip-strip" aria-label="Inbox status filters">{inboxTabs.map((tab) => <button key={tab} className={activeTab === tab ? 'active all' : 'all'} onClick={() => setActiveTab(tab)}>{tab} <span>{tabCounts[tab] || 0}</span></button>)}</div>
        <div className="review-filters">
          <div className="search-input review-search"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, record id, batch id, tag" /></div>
          <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}><option value="all">All sources</option>{sources.map((source) => <option key={source}>{source}</option>)}</select>
          <select value={batchFilter} onChange={(event) => setBatchFilter(event.target.value)}><option value="all">All batches</option>{batches.map((batch) => <option key={batch.batchId} value={batch.batchId}>{batch.name}</option>)}</select>
          <select value={tagFilter} onChange={(event) => setTagFilter(event.target.value)}><option value="all">All tags</option>{allTags.map((tag) => <option key={tag}>{tag}</option>)}</select>
          <select value={assigneeFilter} onChange={(event) => setAssigneeFilter(event.target.value)}><option value="all">All assignees</option>{assignees.map((user) => <option key={user.user_id} value={user.user_id}>{user.full_name}</option>)}</select>
        </div>
        {activeTab === 'Batches' ? (
          <div className="batch-grid">{batches.map((batch) => <button key={batch.batchId} className="batch-card" onClick={() => { setActiveTab('All'); setBatchFilter(batch.batchId); }}><strong>{batch.name}</strong><span>{batch.batchId}</span><p>{batch.rows.length} total · {batch.rows.filter((row) => row.statusLabel === 'Published').length} published · {batch.rows.filter((row) => row.statusLabel === 'Needs Review').length} needs review</p><div className="score-bar"><span style={{ width: `${percentValue(batch.rows.filter((row) => row.statusLabel === 'Published').length, batch.rows.length)}%` }} /></div></button>)}</div>
        ) : (
          <table className="data-table review-table inbox-table"><thead><tr><th>Record</th><th>Batch</th><th>Date/time</th><th>Name & summary</th><th>Status</th><th>Tags</th><th>Comments</th><th>Assignee</th><th>Actions</th></tr></thead><tbody>
            {filtered.map((row) => <tr key={row.id} className="clickable-row" onClick={() => setDetail(row)}><td>{row.id}</td><td>{row.batchId}</td><td>{formatDate(row.updatedAt)}</td><td><strong>{row.name}</strong><span className="subtle-row-note">{row.summary}</span></td><td><span className={`pill ${statusTone(row.statusLabel)}`}>{row.statusLabel}</span></td><td><InlineTags tags={tags[row.id] || row.tags} onAdd={(tag) => setTags((current) => ({ ...current, [row.id]: [...(current[row.id] || row.tags), tag] }))} /></td><td><InlineComments comments={comments[row.id] || row.comments} onAdd={(text) => setComments((current) => ({ ...current, [row.id]: [...(current[row.id] || row.comments), text] }))} /></td><td><span className="avatar-dot">{row.assigneeName.split(' ').map((part) => part[0]).join('').slice(0, 2)}</span> {row.assigneeName}</td><td onClick={(event) => event.stopPropagation()}><InboxActions row={row} onApprove={() => onApprove(row.review)} onTransition={transition} onOpenEntity={onOpenEntity} onCreateGsr={onCreateGsr} /></td></tr>)}
          </tbody></table>
        )}
      </section>
      {detail && <InboxDetailDrawer row={{ ...detail, tags: tags[detail.id] || detail.tags, comments: comments[detail.id] || detail.comments }} assignees={assignees} onClose={() => setDetail(null)} onOpenEntity={onOpenEntity} onApprove={() => onApprove(detail.review)} onTransition={transition} onComment={(text) => setComments((current) => ({ ...current, [detail.id]: [...(current[detail.id] || detail.comments), text] }))} onTag={(tag) => setTags((current) => ({ ...current, [detail.id]: [...(current[detail.id] || detail.tags), tag] }))} />}
    </>
  );
}

interface InboxRow {
  id: string;
  batchId: string;
  source: string;
  updatedAt?: number;
  name: string;
  summary: string;
  statusLabel: string;
  tags: string[];
  comments: string[];
  assigneeId: string;
  assigneeName: string;
  dataxId?: string;
  review: ReviewItem;
}

function buildInboxRows(activities: Activity[], reviews: ReviewItem[], suppliers: Supplier[]): InboxRow[] {
  const reviewMap = new Map(reviews.map((review) => [review.source_record_id, review]));
  return activities.map((activity, index) => {
    const review = reviewMap.get(activity.source_record_id);
    const statusMap: Record<string, string> = { matched: 'Published', review: 'Needs Review', new: 'In Queue', pending: 'In Progress', failed: 'Insufficient data', rework: 'Rework', rejected: 'Cancelled', outlier: 'Other' };
    const state = sourceState(activity).label;
    const supplier = suppliers[index % Math.max(1, suppliers.length)];
    return {
      id: activity.source_record_id,
      batchId: `B-${String(index % 4 + 1).padStart(3, '0')}`,
      source: activity.source_name || review?.source_name || 'Manual',
      updatedAt: activity.updated_at || review?.updated_at || activity.created_at,
      name: activity.source_supplier_name || review?.source_supplier_name || supplier?.master_legal_name || 'Inbound record',
      summary: activity.match_summary || review?.match_summary || `Candidate ${activity.master_legal_name || review?.candidate_name || supplier?.master_legal_name || 'pending'}`,
      statusLabel: statusMap[state] || 'In Queue',
      tags: index % 3 === 0 ? ['high-priority', 'erp'] : index % 3 === 1 ? ['tax-review'] : ['manual'],
      comments: [activity.comments || review?.comments || 'No comments yet.'],
      assigneeId: activity.assigned_to_user_id || review?.assigned_to_user_id || '',
      assigneeName: activity.assigned_to_name || review?.assigned_to_name || 'Unassigned',
      dataxId: activity.datax_id || review?.candidate_datax_id,
      review: {
        review_id: review?.review_id || activity.review_id || activity.source_record_id,
        source_record_id: activity.source_record_id,
        source_supplier_name: activity.source_supplier_name,
        source_name: activity.source_name,
        match_score: activity.match_score || review?.match_score || 0,
        candidate_datax_id: activity.datax_id || review?.candidate_datax_id || '',
        candidate_name: activity.master_legal_name || review?.candidate_name || '',
      },
    };
  });
}

function InlineTags({ tags, onAdd }: { tags: string[]; onAdd: (tag: string) => void }) {
  return <div className="inline-tags">{tags.map((tag) => <span key={tag} className="tag-chip">{tag}</span>)}<button onClick={() => onAdd('watch')}>+</button></div>;
}

function InlineComments({ comments, onAdd }: { comments: string[]; onAdd: (text: string) => void }) {
  return <button className="comment-pill" onClick={() => onAdd('Follow-up added inline.')}>{comments.length} comments</button>;
}

function InboxActions({ row, onApprove, onTransition, onOpenEntity, onCreateGsr }: { row: InboxRow; onApprove: () => void; onTransition: (row: InboxRow, status: string) => void; onOpenEntity: (id: string) => void; onCreateGsr: (seed: GsrSeed) => void }) {
  return <select value="" onChange={(event) => {
    const value = event.target.value;
    if (value === 'approve') onApprove();
    if (value === 'match' && row.dataxId) onOpenEntity(row.dataxId);
    if (value === 'edit') onCreateGsr({ source_record_id: row.id, source_supplier_name: row.name, candidate_datax_id: row.dataxId, candidate_name: row.review.candidate_name, match_score: row.review.match_score });
    if (value && !['approve', 'match', 'edit'].includes(value)) onTransition(row, value);
  }}><option value="">•••</option><option value="edit">Edit</option><option value="approve">Approve</option><option value="match">Match</option><option value="rework">Rework</option><option value="duplicate">Duplicate</option><option value="insufficient_data">Insufficient data</option><option value="cancelled">Cancelled</option><option value="other">Other</option><option value="in_queue">Submit</option><option value="published">Publish</option></select>;
}

function InboxDetailDrawer({ row, assignees, onClose, onOpenEntity, onApprove, onTransition, onComment, onTag }: { row: InboxRow; assignees: TenantUser[]; onClose: () => void; onOpenEntity: (id: string) => void; onApprove: () => void; onTransition: (row: InboxRow, status: string) => void; onComment: (text: string) => void; onTag: (tag: string) => void }) {
  const proposed = [
    ['Legal name', row.name, row.review.candidate_name || row.name, row.source, 92],
    ['DUNS', 'missing', '93-978-1519', 'D&B', 88],
    ['NAICS', 'missing', '722511', 'Registry', 81],
  ];
  return (
    <div className="drawer-backdrop" role="dialog" aria-modal="true" aria-label="Inbox record detail" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="source-drawer inbox-detail">
        <div className="drawer-header">
          <div>
            <h2>{row.name}</h2>
            <div className="drawer-status-line"><span className="source-id">{row.id}</span><span className={`pill ${statusTone(row.statusLabel)}`}>{row.statusLabel}</span><span>{row.batchId}</span></div>
          </div>
          <button className="icon-button" onClick={onClose}>×</button>
        </div>
        <div className="drawer-assignee-row">
          <span>Assign to tenant user</span>
          <select className="assignee-select" defaultValue={row.assigneeId} onChange={(event) => announceAction(event.target.value ? `Assigned ${row.id} to selected steward.` : `${row.id} unassigned.`)}>
            <option value="">Unassigned</option>
            {assignees.map((user) => <option key={user.user_id} value={user.user_id}>{user.full_name}</option>)}
          </select>
        </div>
        <div className="drawer-body">
          <section className="drawer-section">
            <span className="card-kicker">Changed / proposed fields</span>
            <div className="proposal-list">
              {proposed.map(([field, current, next, source, confidence]) => (
                <div className="proposal-row" key={field}>
                  <strong>{field}</strong><span>{current}</span><span>{next}</span><em>{source} · {confidence}%</em>
                  <button onClick={() => announceAction(`${field} proposal accepted for ${row.id}.`)}>Accept</button>
                  <button onClick={() => announceAction(`${field} proposal rejected for ${row.id}.`)}>Reject</button>
                </div>
              ))}
            </div>
          </section>
          <section className="drawer-section">
            <span className="card-kicker">Full record</span>
            <div className="drawer-facts"><Meta label="Source" value={row.source} /><Meta label="Batch" value={row.batchId} /><Meta label="Summary" value={row.summary} /><Meta label="Candidate" value={row.review.candidate_name || 'None'} /></div>
            <button className="secondary-button" onClick={() => onComment('Inline edit requested.')}>Edit or add fields inline</button>
          </section>
          <section className="drawer-section"><span className="card-kicker">Tags & comments</span><InlineTags tags={row.tags} onAdd={onTag} /><InlineComments comments={row.comments} onAdd={onComment} /></section>
        </div>
        <div className="drawer-actions"><button className="secondary-button" onClick={() => onTransition(row, 'in_queue')}>Submit</button><button className="primary-button severity-success" onClick={onApprove}>Approve</button><button className="secondary-button severity-info" onClick={() => row.dataxId && onOpenEntity(row.dataxId)}>Match</button><button className="secondary-button severity-danger" onClick={() => onTransition(row, 'rework')}>Rework</button><button className="secondary-button" onClick={() => onTransition(row, 'duplicate')}>Duplicate</button><button className="secondary-button" onClick={() => onTransition(row, 'insufficient_data')}>Insufficient data</button><button className="ghost-button severity-danger" onClick={() => onTransition(row, 'cancelled')}>Cancel</button><small>Survivorship rules + audit on apply.</small></div>
      </aside>
    </div>
  );
}

function GsrAgreementsTab({ scenario }: any) {
  const certs = scenario.agreements.certifications || [];
  return (
    <section className="profile-section">
      <div className="profile-card-grid">
        <article className="info-card identity-card"><span className="card-kicker">NDA</span><strong>{scenario.agreements.nda}</strong><button className="secondary-button severity-secondary" onClick={() => announceAction('NDA renewal workflow opened.')}>Renew</button></article>
        <article className="info-card identity-card"><span className="card-kicker">MSA</span><strong>{scenario.agreements.msa}</strong><button className="secondary-button severity-secondary" onClick={() => announceAction('MSA renewal workflow opened.')}>Renew</button></article>
        <article className="info-card identity-card"><span className="card-kicker">Tier</span><strong>{scenario.agreements.preferredTier}</strong></article>
        <article className="info-card identity-card"><span className="card-kicker">Payment terms</span><strong>{scenario.agreements.paymentTerms}</strong></article>
      </div>
      <div className="info-card identity-card">
        <span className="card-kicker">Contracted categories</span>
        <div className="risk-bars">
          {scenario.agreements.categories.map((c: any) => <div key={c.name}><span>{c.name}</span><div><i style={{ width: `${c.spend}%` }} /></div><strong>{c.spend}%</strong></div>)}
        </div>
      </div>
      <div className="profile-card-grid">
        <div className="info-card identity-card"><span className="card-kicker">Insurance certificates</span>{(scenario.agreements.insurance.length ? scenario.agreements.insurance : [{ name: 'No insurance certificates loaded', expires: '' }]).map((i: any) => <p key={i.name}>{i.name}{i.expires ? ` · ${i.expires}` : ''}</p>)}</div>
        <div className="info-card identity-card"><span className="card-kicker">Certifications</span>{(certs.length ? certs : ['No certifications loaded']).map((c: any, idx: number) => <p key={typeof c === 'string' ? c : c.supplier_certification_id || idx}>{typeof c === 'string' ? c : c.certification_name}</p>)}</div>
        <div className="info-card identity-card"><span className="card-kicker">PO history</span><p>{scenario.agreements.poHistory.total} orders · {scenario.agreements.poHistory.onTime} on-time · {scenario.agreements.poHistory.avgCycle} avg cycle</p></div>
      </div>
    </section>
  );
}

function GsrHistoryTab({ auditEvents, corporateEvents, filings }: any) {
  return (
    <section className="profile-section">
      <div className="info-card identity-card">
        <span className="card-kicker">Regulatory filings</span>
        {(filings?.length ? filings : [{ date: '—', type: 'No filings loaded', source: '' }]).map((f: any) => <p key={`${f.date}-${f.type}`}>{f.date} · {f.type} {f.source ? `· ${f.source}` : ''}</p>)}
      </div>
      <div className="info-card identity-card">
        <span className="card-kicker">Corporate events</span>
        {(corporateEvents.length ? corporateEvents : [{ summary: 'No events recorded this session.' }]).map((event: any) => (
          <div key={event.id || event.summary} className="history-event">
            <p>{event.summary}</p>
            {event.event_date && <small>{event.event_date}</small>}
            {Array.isArray(event.details) && event.details.filter(Boolean).map((d: string, i: number) => <small key={`${event.id}-d-${i}`}>{d}</small>)}
          </div>
        ))}
      </div>
      <AuditView auditEvents={auditEvents} embedded />
    </section>
  );
}


const emptyPlaygroundForm = {
  duns: '12-3456789',
  tax_id: '13-2345678',
  erp_vendor_id: 'V-00045',
  id_scheme: 'SAP-HILTON-PROD',
  legal_name: 'Acme Hospitality Supplies',
  website: 'acmehospitality.com',
  city: 'New York',
  country: 'US',
  address: '',
  postal_code: '',
  phone: '',
  email: '',
};

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

function laneFor(score: number, auto: number, review: number) {
  if (score >= auto) return { label: 'Auto', tone: 'auto' };
  if (score >= review) return { label: 'Review', tone: 'review' };
  return { label: 'Miss', tone: 'miss' };
}

function ResolveLanding({ config, onOpenPlayground, onCommitted }: { config: any; onOpenPlayground: () => void; onCommitted: () => void }) {
  const [form, setForm] = useState(emptyPlaygroundForm);
  const [mode, setMode] = useState<'dry-run' | 'commit' | 'sync'>('dry-run');
  const [result, setResult] = useState<any | null>(null);
  const [selectedDataxId, setSelectedDataxId] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const candidates = result?.candidates || [];

  function setField(field: keyof typeof emptyPlaygroundForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function payload() {
    const identifiers = [
      form.duns.trim() && { id_type: 'duns', id_value: form.duns.trim() },
      form.tax_id.trim() && { id_type: 'tax_id', id_value: form.tax_id.trim(), jurisdiction: form.country.trim() || undefined },
      form.erp_vendor_id.trim() && { id_type: 'erp_vendor_id', id_value: form.erp_vendor_id.trim(), id_scheme: form.id_scheme.trim() || undefined },
    ].filter(Boolean);
    return {
      tenant_id: TENANT_ID,
      identifiers,
      firmographics: {
        legal_name: form.legal_name.trim() || undefined,
        website: form.website.trim() || undefined,
        city: form.city.trim() || undefined,
        country: form.country.trim() || undefined,
        jurisdiction: form.country.trim() || undefined,
        address: form.address.trim() || undefined,
        postal_code: form.postal_code.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
      },
      auto_threshold: Number(config.auto_merge_threshold || 0.9),
      review_threshold: Number(config.review_threshold || 0.65),
      candidate_limit: 5,
      explain_all: true,
      create_if_missing: false,
    };
  }

  async function runResolve() {
    setLoading(true);
    setError('');
    try {
      const data = await api<any>('/api/suppliers/resolve', {
        method: 'POST',
        body: JSON.stringify(payload()),
      });
      setResult(data);
      setSelectedDataxId(data.datax_id || data.candidates?.[0]?.datax_id || '');
      if (mode !== 'dry-run') setConfirming(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resolve failed');
    } finally {
      setLoading(false);
    }
  }

  async function commitResolve(action: 'link' | 'review' | 'create_candidate' | 'outlier' = 'review') {
    setLoading(true);
    setError('');
    try {
      await api('/api/suppliers/resolve/commit', {
        method: 'POST',
        body: JSON.stringify({
          ...payload(),
          action,
          selected_datax_id: selectedDataxId || undefined,
          sync: mode === 'sync',
          reason: mode === 'sync' ? 'Single resolve and sync' : 'Single resolve commit',
        }),
      });
      setConfirming(false);
      onCommitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Commit failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="resolve-landing">
      <div className="page-heading compact">
        <div>
          <div className="breadcrumb">Home › Resolve</div>
          <h1>Resolve supplier</h1>
          <p>Run a single supplier through deterministic identifiers and the matcher before writing anything.</p>
        </div>
        <button className="secondary-button" onClick={onOpenPlayground}>Open full playground</button>
      </div>

      <section className="panel resolve-entry-panel">
        <div className="segmented-control">
          {[
            ['dry-run', 'Dry-run'],
            ['commit', 'Resolve & commit'],
            ['sync', 'Resolve & sync'],
          ].map(([value, label]) => (
            <button key={value} className={mode === value ? 'active' : ''} onClick={() => setMode(value as any)}>{label}</button>
          ))}
        </div>
        <div className="playground-grid two">
          <label>Legal name<input value={form.legal_name} onChange={(e) => setField('legal_name', e.target.value)} /></label>
          <label>Website<input value={form.website} onChange={(e) => setField('website', e.target.value)} /></label>
          <label>DUNS<input value={form.duns} onChange={(e) => setField('duns', e.target.value)} /></label>
          <label>Tax ID / EIN<input value={form.tax_id} onChange={(e) => setField('tax_id', e.target.value)} /></label>
          <label>City<input value={form.city} onChange={(e) => setField('city', e.target.value)} /></label>
          <label>Country<input value={form.country} onChange={(e) => setField('country', e.target.value)} /></label>
        </div>
        <button className="primary-button severity-primary" disabled={loading} onClick={runResolve}>{loading ? 'Resolving...' : mode === 'dry-run' ? 'Run dry-run resolve' : 'Resolve before commit'}</button>
        {error && <div className="playground-error">{error}</div>}
      </section>

      {result && (
        <section className="panel resolve-result-panel">
          <div className="table-card-header">
            <h2>{result.resolved ? 'Resolved candidate' : 'Candidate results'}</h2>
            <span>{result.match_type || 'probabilistic'}</span>
          </div>
          <div className="candidate-stack">
            {(candidates.length ? candidates : [{ datax_id: result.datax_id, legal_name: result.entity?.legal_name, confidence: result.confidence, summary: result.signals?.[0]?.detail }]).filter((candidate: any) => candidate.datax_id).map((candidate: any) => (
              <label className="resolve-candidate-choice" key={candidate.datax_id}>
                <input type="radio" name="selectedDataxId" checked={selectedDataxId === candidate.datax_id} onChange={() => setSelectedDataxId(candidate.datax_id)} />
                <strong>{candidate.legal_name || candidate.entity?.legal_name || candidate.datax_id}</strong>
                <span>{candidate.datax_id} · {fmtScore(Number(candidate.probability ?? candidate.confidence ?? result.confidence ?? 0))}</span>
                <em>{candidate.summary || 'Resolved by identifier or matcher'}</em>
              </label>
            ))}
          </div>
          {mode !== 'dry-run' && (
            <div className="wizard-actions">
              <button className="secondary-button" onClick={() => commitResolve('review')} disabled={loading}>Send to review</button>
              <button className="secondary-button severity-secondary" onClick={() => commitResolve('create_candidate')} disabled={loading}>Create new GSR candidate</button>
              <button className="ghost-button severity-danger" onClick={() => commitResolve('outlier')} disabled={loading}>Mark outlier</button>
              <button className="primary-button severity-success" onClick={() => commitResolve('link')} disabled={loading || !selectedDataxId}>{mode === 'sync' ? 'Commit & queue sync' : 'Commit link'}</button>
            </div>
          )}
        </section>
      )}

      {confirming && (
        <div className="inline-confirm">
          <strong>{mode === 'sync' ? 'Resolve & sync requires confirmation.' : 'Resolve & commit requires confirmation.'}</strong>
          <span>Choose an action in the result panel. Dry-run has no side effects.</span>
        </div>
      )}
    </div>
  );
}

function ResolvePlayground({ config, setConfig, onSaved }: {
  config: any;
  setConfig: (config: any) => void;
  onSaved: (config: any) => void;
}) {
  const [form, setForm] = useState(emptyPlaygroundForm);
  const [autoThreshold, setAutoThreshold] = useState(Number(config.auto_merge_threshold || 0.9));
  const [reviewThreshold, setReviewThreshold] = useState(Number(config.review_threshold || 0.65));
  const [savedConfig, setSavedConfig] = useState<any>(config);
  const [result, setResult] = useState<any | null>(null);
  const [resolverHealth, setResolverHealth] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [saveReason, setSaveReason] = useState('Threshold tuning from Resolve Playground');
  const [candidateLimit, setCandidateLimit] = useState(10);
  const user = currentUser();
  const isSuperAdmin = user?.is_super_admin === true;
  const canSave = isSuperAdmin || user?.role === 'admin';
  const hasFirmographics = !!(form.legal_name.trim() || form.website.trim() || form.city.trim() || form.address.trim());
  const candidates = (result?.candidates || []).slice().sort((a: any, b: any) => Number(b.confidence || 0) - Number(a.confidence || 0));
  const visibleCandidates = candidates.slice(0, candidateLimit);
  const candidateLane = (candidate: any) => result?.conflict && candidate.recommendation === 'Steward review required'
    ? 'review'
    : laneFor(Number(candidate.confidence || 0), autoThreshold, reviewThreshold).tone;

  useEffect(() => {
    setAutoThreshold(Number(config.auto_merge_threshold || 0.9));
    setReviewThreshold(Number(config.review_threshold || 0.65));
    setSavedConfig(config);
  }, [config]);

  useEffect(() => {
    let cancelled = false;
    api<{ config: any }>(`/api/match-merge/suppliers/config?tenant_id=${TENANT_ID}`)
      .then((data) => {
        if (cancelled) return;
        setSavedConfig(data.config);
        setConfig(data.config);
        setAutoThreshold(Number(data.config.auto_merge_threshold || 0.9));
        setReviewThreshold(Number(data.config.review_threshold || 0.65));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api<any>(`/api/suppliers/resolve/health?tenant_id=${TENANT_ID}`)
      .then((data) => {
        if (!cancelled) setResolverHealth(data);
      })
      .catch(() => {
        if (!cancelled) setResolverHealth(null);
      });
    return () => { cancelled = true; };
  }, []);

  function setField(field: keyof typeof emptyPlaygroundForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updateAuto(value: number) {
    setAutoThreshold(Math.max(value, reviewThreshold));
  }

  function updateReview(value: number) {
    setReviewThreshold(Math.min(value, autoThreshold));
  }

  async function runResolve() {
    setLoading(true);
    setError('');
    try {
      const identifiers = [
        form.duns.trim() && { id_type: 'duns', id_value: form.duns.trim() },
        form.tax_id.trim() && { id_type: 'tax_id', id_value: form.tax_id.trim(), jurisdiction: form.country.trim() || undefined },
        form.erp_vendor_id.trim() && { id_type: 'erp_vendor_id', id_value: form.erp_vendor_id.trim(), id_scheme: form.id_scheme.trim() || undefined },
      ].filter(Boolean);
      const data = await api<any>('/api/suppliers/resolve', {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: TENANT_ID,
          identifiers,
          firmographics: {
            legal_name: form.legal_name.trim() || undefined,
            website: form.website.trim() || undefined,
            city: form.city.trim() || undefined,
            country: form.country.trim() || undefined,
            jurisdiction: form.country.trim() || undefined,
            address: form.address.trim() || undefined,
            postal_code: form.postal_code.trim() || undefined,
            phone: form.phone.trim() || undefined,
            email: form.email.trim() || undefined,
          },
          auto_threshold: autoThreshold,
          review_threshold: reviewThreshold,
          candidate_limit: candidateLimit,
          explain_all: true,
          create_if_missing: false,
        }),
      });
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resolve failed');
    } finally {
      setLoading(false);
    }
  }

  async function saveConfig() {
    const nextConfig = {
      ...savedConfig,
      tenant_id: TENANT_ID,
      auto_merge_threshold: autoThreshold,
      review_threshold: reviewThreshold,
      reason: saveReason,
    };
    await api('/api/match-merge/suppliers/config', {
      method: 'PATCH',
      body: JSON.stringify(nextConfig),
    });
    setSavedConfig(nextConfig);
    setShowSaveConfirm(false);
    onSaved(nextConfig);
  }

  return (
    <div className="playground-page">
      <div className="page-heading compact playground-heading">
        <div>
          <p className="breadcrumb">Resolution Playground</p>
          <h1>Dry-run supplier resolve</h1>
          <p>Test identifiers and firmographics against the production resolver without creating, linking, or merging records.</p>
        </div>
        <div className="dry-run-pill"><span /> dry-run</div>
      </div>

      <div className="playground-layout">
        <section className="playground-card playground-input-card">
          <h2><span className="box-glyph" /> Identifiers</h2>
          <div className="playground-grid two">
            <label>DUNS<input value={form.duns} onChange={(e) => setField('duns', e.target.value)} /></label>
            <label>Tax ID / EIN<input value={form.tax_id} onChange={(e) => setField('tax_id', e.target.value)} /></label>
            <label>ERP vendor ID<input value={form.erp_vendor_id} onChange={(e) => setField('erp_vendor_id', e.target.value)} /></label>
            <label>Scheme<input value={form.id_scheme} onChange={(e) => setField('id_scheme', e.target.value)} /></label>
          </div>

          <h2><span className="box-glyph" /> Firmographics</h2>
          <div className="playground-grid">
            <label>Legal name<input value={form.legal_name} onChange={(e) => setField('legal_name', e.target.value)} /></label>
            <label>Website<input value={form.website} onChange={(e) => setField('website', e.target.value)} /></label>
          </div>
          <div className="playground-grid two">
            <label>City<input value={form.city} onChange={(e) => setField('city', e.target.value)} /></label>
            <label>Country<input value={form.country} onChange={(e) => setField('country', e.target.value)} /></label>
          </div>
          <details className="playground-extra">
            <summary>More firmographics</summary>
            <div className="playground-grid">
              <label>Address<input value={form.address} onChange={(e) => setField('address', e.target.value)} /></label>
            </div>
            <div className="playground-grid two">
              <label>Postal code<input value={form.postal_code} onChange={(e) => setField('postal_code', e.target.value)} /></label>
              <label>Phone<input value={form.phone} onChange={(e) => setField('phone', e.target.value)} /></label>
            </div>
            <label>Email<input value={form.email} onChange={(e) => setField('email', e.target.value)} /></label>
          </details>
          <label className="playground-result-limit">
            Show top matches
            <select value={candidateLimit} onChange={(event) => setCandidateLimit(Number(event.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={100}>100</option>
            </select>
          </label>
          <button className="playground-run-button" disabled={loading} onClick={runResolve}>{loading ? 'Resolving...' : 'Run dry-run resolve'}</button>
          <p className="playground-helper">No create, link, or merge - read-only</p>
        </section>

        <section className="playground-results">
          {result?.deterministic_result && (
            <div className="deterministic-banner">
              <div>
                <h2><span className="box-glyph blue" /> Resolved deterministically</h2>
                <p>Matched on {(result.deterministic_result.matched_on || []).join(', ')} exact {'->'} {result.deterministic_result.datax_id}. thresholds don't apply to deterministic hits</p>
              </div>
              <strong><span>confidence</span>1.00</strong>
            </div>
          )}

          {result?.conflict && (
            <div className="conflict-banner">
              <div>
                <h2><span className="box-glyph" /> Identifier / firmographic conflict</h2>
                <p>{result.conflict_summary || 'Exact identifiers and firmographics point to different suppliers. Route this record to steward review before linking.'}</p>
              </div>
              <strong>Review</strong>
            </div>
          )}

          {!result?.deterministic_result && !candidates.length && (
            <div className="playground-empty">
              <strong>Paste identifiers or firmographics to run a dry resolve.</strong>
              <p>{hasFirmographics ? 'Results will appear here with per-signal contribution bars.' : 'Identifier-only input can resolve deterministically; add legal name, website, and location to see fuzzy decomposition.'}</p>
            </div>
          )}

          {error && <div className="playground-error">{error}</div>}

          {resolverHealth?.ready && (
            <div className="playground-health">
              Resolver ready: {resolverHealth.supplier_master_count} GSRs indexed
              <span>{resolverHealth.supplier_identifier_count} active identifiers</span>
            </div>
          )}

          <div className="playground-thresholds">
            <label>Auto threshold <strong>{fmtScore(autoThreshold)}</strong><input type="range" min="0" max="1" step="0.01" value={autoThreshold} onChange={(e) => updateAuto(Number(e.target.value))} /></label>
            <label>Review threshold <strong>{fmtScore(reviewThreshold)}</strong><input type="range" min="0" max="1" step="0.01" value={reviewThreshold} onChange={(e) => updateReview(Number(e.target.value))} /></label>
          </div>

          {!!candidates.length && (
            <>
              <div className="playground-counts">
                <span><b>{candidates.filter((c: any) => candidateLane(c) === 'auto').length}</b> auto</span>
                <span><b>{candidates.filter((c: any) => candidateLane(c) === 'review').length}</b> review</span>
                <span><b>{candidates.filter((c: any) => candidateLane(c) === 'miss').length}</b> miss</span>
              </div>
              <div className="fuzzy-heading-row">
                <h2 className="fuzzy-heading">{result?.conflict ? 'Competing fuzzy candidates' : 'Fuzzy candidates'} {result?.deterministic_result && !result?.conflict ? '(explain-all - shown despite deterministic hit)' : ''}</h2>
                <span>Showing top {Math.min(candidateLimit, candidates.length).toLocaleString()} of {candidates.length.toLocaleString()}</span>
              </div>
              <div className="candidate-stack">
                {visibleCandidates.map((candidate: any, index: number) => <PlaygroundCandidate key={candidate.datax_id || candidate.candidate?.datax_id || index} candidate={candidate} auto={autoThreshold} review={reviewThreshold} forceReview={!!result?.conflict && candidate.recommendation === 'Steward review required'} />)}
              </div>
            </>
          )}

          <div className="playground-save-row">
            <button className="playground-save-button" disabled={!canSave} onClick={() => setShowSaveConfirm(true)}>Save thresholds</button>
            <p>{canSave ? 'Admin save changes live thresholds.' : 'Manager role - tune & inspect only; admin required to save'}</p>
          </div>
        </section>
      </div>

      {showSaveConfirm && (
        <div className="wizard-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowSaveConfirm(false); }}>
          <div className="wizard-page save-threshold-modal">
            <WizardHeader title="Save thresholds" step={1} labels={['Confirm']} onClose={() => setShowSaveConfirm(false)} />
            <p>This changes live auto-merge behavior for the current workspace.</p>
            <div className="threshold-delta-grid">
              <Meta label="Review" value={`${fmtScore(Number(savedConfig.review_threshold || 0.65))} -> ${fmtScore(reviewThreshold)}`} />
              <Meta label="Auto" value={`${fmtScore(Number(savedConfig.auto_merge_threshold || 0.9))} -> ${fmtScore(autoThreshold)}`} />
            </div>
            <label>Reason<textarea value={saveReason} onChange={(e) => setSaveReason(e.target.value)} /></label>
            <div className="wizard-actions"><button className="secondary-button" onClick={() => setShowSaveConfirm(false)}>Cancel</button><button className="primary-button" onClick={saveConfig}>Confirm save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaygroundCandidate({ candidate, auto, review, forceReview = false }: { candidate: any; auto: number; review: number; forceReview?: boolean }) {
  const lane = forceReview ? { label: 'Review', tone: 'review' } : laneFor(Number(candidate.confidence || 0), auto, review);
  const rawComparisons = candidate.perComparison || candidate.per_comparison || [];
  const hasComparison = (name: string) => rawComparisons.some((item: any) => item.comparison === name || item.label === name);
  const comparisons = [
    ...rawComparisons,
    ...['name', 'address'].filter((name) => !hasComparison(name)).map((name) => ({ comparison: name, label: name, level: 'null', weight: 0, contribution: 0 })),
  ];
  const positives = comparisons.filter((item: any) => Number(item.weight || 0) > 1);
  const negatives = comparisons.filter((item: any) => Number(item.weight || 0) < -1);
  const neutral = comparisons.filter((item: any) => Math.abs(Number(item.weight || 0)) <= 1);
  const comparisonLabels: Record<string, string> = {
    tax_id: 'Tax ID',
    duns: 'DUNS',
    name: 'Supplier name',
    domain: 'Website/domain',
    address: 'Address',
    phone: 'Phone',
    email: 'Email',
  };
  const comparisonLabel = (signal: any) => comparisonLabels[String(signal.comparison || signal.label || '')] || signal.label || signal.comparison || 'Signal';
  return (
    <article className={`playground-candidate ${lane.tone}`}>
      <div className="candidate-head">
        <div><h3>{candidate.legal_name || candidate.candidate?.master_legal_name || 'Candidate supplier'}</h3><p>{candidate.datax_id}</p></div>
        <div className="candidate-score"><strong>{Number(candidate.confidence || 0).toFixed(2)}</strong><span>{lane.label}</span></div>
      </div>
      <div className="explain-verdict">
        <strong>{candidate.summary || 'Matcher evidence available'}</strong>
        <span>{candidate.tier_label || 'probabilistic'}</span>
      </div>
      {!!comparisons.length && (
        <div className="signal-chip-row">
          {!!positives.length && <span className="matched">Matched: {positives.map((item: any) => item.comparison || item.label).slice(0, 3).join(', ')}</span>}
          {!!neutral.length && <span className="partial">Partial/no info: {neutral.map((item: any) => item.comparison || item.label).slice(0, 3).join(', ')}</span>}
          {!!negatives.length && <span className="negative">Conflicts: {negatives.map((item: any) => item.comparison || item.label).slice(0, 3).join(', ')}</span>}
        </div>
      )}
      <div className="signal-list">
        {(comparisons.length ? comparisons : candidate.signals || []).map((signal: any) => (
          <div className={`signal-row ${Number(signal.weight ?? signal.contribution ?? 0) < 0 ? 'negative' : Number(signal.weight ?? signal.contribution ?? 0) > 0 ? 'positive' : 'neutral'}`} key={`${signal.comparison || signal.label}-${signal.level || ''}`}>
            <span>{comparisonLabel(signal)} {signal.level ? `(${signal.level})` : ''}</span>
            <i><b style={{ width: `${Math.max(2, Math.min(100, Math.abs(Number(signal.weight ?? signal.contribution ?? signal.score ?? 0)) * 9))}%` }} /></i>
            <em>m {Number(signal.m ?? 0).toFixed(4)} / u {Number(signal.u ?? 0).toFixed(4)} · w {Number(signal.weight ?? signal.contribution ?? 0).toFixed(2)}</em>
          </div>
        ))}
      </div>
    </article>
  );
}

function GovernanceView({ config, setConfig, metrics, connections }: { config: any; setConfig: (c: any) => void; metrics: DashboardMetrics; connections: ConnectionSummary[] }) {
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [learning, setLearning] = useState<any>({ labels: [], events: [], precision_trend: [], threshold_recommendations: [] });
  const [showAddConnection, setShowAddConnection] = useState(false);
  const [policyConnections, setPolicyConnections] = useState<ConnectionSummary[]>([]);
  const user = currentUser();
  const canSave = user?.is_super_admin || user?.role === 'admin';
  const routing = inboundRoutingProjection(metrics, connections.length ? connections : fallbackConnections);
  const total = routing.total;
  const thresholdDelta = (Number(config.auto_merge_threshold || 0.9) - 0.9) + ((Number(config.review_threshold || 0.65) - 0.65) * 0.7);
  const autoLinked = Math.max(0, Math.min(total, Math.round(routing.autoLinked - (thresholdDelta * total * 0.9))));
  const needsReview = Math.max(0, Math.min(total - autoLinked, Math.round(routing.needsReview + (thresholdDelta * total * 0.65))));
  const newCandidates = routing.newCandidates;
  const outliers = routing.outliers;
  const pending = Math.max(total - autoLinked - needsReview - newCandidates - outliers, 0);
  const preview = [
    { label: 'Auto-linked', value: autoLinked, tone: 'success' },
    { label: 'Needs review', value: needsReview, tone: 'warning' },
    { label: 'New GSR candidate', value: newCandidates, tone: 'info' },
    { label: 'Outlier / do not learn', value: outliers, tone: 'purple' },
    { label: 'Pending / validation', value: pending, tone: 'gray' },
  ];
  const trustConnections: ConnectionSummary[] = policyConnections.length ? policyConnections : connections.length ? connections : [
    { source_system_id: 'sap', source_name: 'ERP-SAP', connection_type: 'API', trust_score: 0.95, status: 'active', total_records: 900, review_records: 161, matched_records: 329, rejected_records: 22, new_records: 388 },
    { source_system_id: 'oracle', source_name: 'ERP-Oracle', connection_type: 'SFTP', trust_score: 0.95, status: 'active', total_records: 600, review_records: 114, matched_records: 203, rejected_records: 18, new_records: 265 },
    { source_system_id: 'portal', source_name: 'Vendor Portal', connection_type: 'API', trust_score: 0.78, status: 'active', total_records: 600, review_records: 112, matched_records: 206, rejected_records: 22, new_records: 260 },
    { source_system_id: 'ap', source_name: 'AP Legacy', connection_type: 'Manual', trust_score: 0.68, status: 'watch', total_records: 450, review_records: 89, matched_records: 152, rejected_records: 31, new_records: 178 },
  ];
  const thresholdsValid = Number(config.auto_merge_threshold) >= Number(config.review_threshold);

  useEffect(() => {
    api<any>(`/api/learning/insights?tenant_id=${TENANT_ID}`).then(setLearning).catch(() => undefined);
  }, []);

  async function savePolicyConfig() {
    if (!canSave || !thresholdsValid) return;
    setSaving(true);
    setSaveMessage('');
    try {
      await api('/api/match-merge/suppliers/config', {
        method: 'PATCH',
        body: JSON.stringify({
          ...config,
          tenant_id: TENANT_ID,
          reason: 'Resolution policy thresholds updated',
        }),
      });
      setSaveMessage('Thresholds saved.');
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-heading">
        <div>
          <div className="breadcrumb">Home › Resolution policy</div>
          <h1>Resolution policy</h1>
          <p>Tune thresholds, trust, and routing behavior before changes hit production flow.</p>
        </div>
      </div>

      <div className="policy-layout">
        <section className="panel governance policy-controls">
          <div className="policy-rule-header">
            <h2>Resolution thresholds</h2>
          </div>
          <label>
            Auto-link threshold
            <input type="range" min="0.5" max="1" step="0.01" value={config.auto_merge_threshold} onChange={(e) => setConfig({ ...config, auto_merge_threshold: Math.max(Number(e.target.value), Number(config.review_threshold || 0.65)) })} />
            <strong>{fmtScore(config.auto_merge_threshold)}</strong>
            <span>Records above this score link automatically - no human review.</span>
          </label>
          <label>
            Review threshold
            <input type="range" min="0.3" max="0.95" step="0.01" value={config.review_threshold} onChange={(e) => setConfig({ ...config, review_threshold: Math.min(Number(e.target.value), Number(config.auto_merge_threshold || 0.9)) })} />
            <strong>{fmtScore(config.review_threshold)}</strong>
            <span>Below auto-link but above this enters the review queue.</span>
          </label>
          <div className="policy-save-row">
            <button className="primary-button" disabled={!canSave || !thresholdsValid || saving} onClick={savePolicyConfig}>
              {saving ? 'Saving...' : 'Save thresholds'}
            </button>
            <span>{canSave ? (saveMessage || 'Saved values update match_merge_config.') : 'Admin role required to save.'}</span>
          </div>
        </section>

        <section className="panel policy-impact">
          <div className="panel-header compact-panel-header">
            <div>
              <h2>Live impact preview</h2>
              <p>Approximate routing for {total.toLocaleString()} inbound records with the current thresholds.</p>
            </div>
          </div>
          <div className="impact-stack">
            {preview.map((item) => (
              <div key={item.label} className={`impact-segment ${item.tone}`} style={{ flexGrow: Math.max(item.value, 1) }}>
                <strong>{item.value.toLocaleString()}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div className="impact-legend">
            {preview.map((item) => (
              <span key={item.label}><i className={item.tone} />{item.label}</span>
            ))}
          </div>
        </section>
      </div>

      <section className="panel policy-source-table">
        <div className="panel-header compact-panel-header">
          <div>
            <span className="section-label">Source trust scores</span>
            <p>Higher-trust systems can carry more weight during conflict resolution; review and failure rates expose drift.</p>
          </div>
          <button className="secondary-button severity-secondary" onClick={() => setShowAddConnection(true)}>+ Add connection</button>
        </div>
        <div className="connection-health-table policy">
          {trustConnections.map((connection) => {
            const trust = normalizedTrustScore(connection.trust_score);
            return (
              <article className="connection-health-row" key={connection.source_system_id}>
                <div>
                  <strong>{connection.source_name}</strong>
                  <span>{connection.connection_type} · {connection.status}</span>
                </div>
                <Meta label="Records" value={(connection.total_records || 0).toLocaleString()} />
                <Meta label="Review rate" value={`${percentValue(connection.review_records, connection.total_records)}%`} />
                <Meta label="Failure rate" value={`${percentValue(connection.rejected_records, connection.total_records)}%`} />
                <div className="trust-meter">
                  <span>Trust score</span>
                  <div><i style={{ width: `${trust}%` }} /></div>
                  <strong>{trust}%</strong>
                </div>
                <button className="ghost-button" onClick={() => {
                  setConfig({ ...config, review_threshold: Math.max(0.3, Number(config.review_threshold || 0.65) - 0.01) });
                  announceAction(`${connection.source_name} trust tuning applied to the policy preview.`);
                }}>Tune</button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="panel policy-source-table">
        <div className="panel-header compact-panel-header">
          <div>
            <span className="section-label">Learning insights</span>
            <p>Confirmed decisions feed matcher labels and tenant-scoped learning. Tenant-private confirmations stay private.</p>
          </div>
          <button className="secondary-button severity-danger" onClick={() => api('/api/learning/unlearn', { method: 'POST', body: JSON.stringify({ tenant_id: TENANT_ID, reason: 'Manual correction from learning insights' }) }).then(() => setSaveMessage('Correction recorded.'))}>Record correction</button>
        </div>
        <div className="profile-card-grid">
          {(learning.labels?.length ? learning.labels : [{ label: 'match', count: 0, eligible_for_shared: 0 }, { label: 'non_match', count: 0, eligible_for_shared: 0 }]).map((item: any, index: number) => (
            <article className="info-card identity-card" key={`${item.label}-${index}`}>
              <span className="card-kicker">{item.eligible_for_shared ? 'Shared eligible' : 'Tenant private'}</span>
              <strong>{Number(item.count || 0).toLocaleString()}</strong>
              <p>{item.label}</p>
            </article>
          ))}
        </div>
        <table className="data-table compact-table"><thead><tr><th>Learning event</th><th>GSR</th><th>Scope</th><th>When</th></tr></thead><tbody>
          {(learning.events?.length ? learning.events : [{ learning_event_id: 'none', event_type: 'No learning events yet', datax_id: '-', eligible_for_shared: 0, created_at: null }]).map((event: any) => (
            <tr key={event.learning_event_id}><td>{String(event.event_type).replace('_', ' ')}</td><td>{event.datax_id || '-'}</td><td>{event.eligible_for_shared ? 'shared' : 'tenant private'}</td><td>{event.created_at ? formatDate(event.created_at) : '-'}</td></tr>
          ))}
        </tbody></table>
      </section>
      {showAddConnection && (
        <AddConnectionWizard
          onClose={() => setShowAddConnection(false)}
          onSave={(row) => {
            setPolicyConnections((prev) => [
              ...(prev.length ? prev : trustConnections),
              {
                source_system_id: String(row.name || `source-${Date.now()}`).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                source_name: row.name || 'New connection',
                connection_type: row.method || 'Manual',
                trust_score: Number(row.trust || 75) / 100,
                status: row.status || 'active',
                total_records: Number(row.records || 0),
                review_records: 0,
                matched_records: 0,
                rejected_records: 0,
                new_records: 0,
              },
            ]);
            setShowAddConnection(false);
            announceAction(`${row.name || 'New connection'} added to policy trust scores.`);
          }}
        />
      )}
    </>
  );
}

function AuditView({ auditEvents, embedded = false }: { auditEvents: any[]; embedded?: boolean }) {
  const rows = auditEvents.length ? auditEvents : [
    { event_type: 'auto_match', changed_by_name: 'System', changed_at: 1769302800, reason: 'System auto-match high confidence', source_record_id: 'XRF-002213' },
    { event_type: 'manual_merge', changed_by_name: 'Elizabeth Rodriguez', changed_at: 1769302800, reason: 'Exact match on Tax ID', source_record_id: 'XRF-002764' },
    { event_type: 'outlier', changed_by_name: 'Elizabeth Rodriguez', changed_at: 1769302800, reason: 'Corrupted address format', source_record_id: 'XRF-002374' },
    { event_type: 'manual_merge', changed_by_name: 'Jessica Jones', changed_at: 1769302800, reason: 'Exact match on Tax ID', source_record_id: 'XRF-000951' },
    { event_type: 'remap', changed_by_name: 'Marcus Rivera', changed_at: 1769302800, reason: 'Incorrect mapping identified by business unit', source_record_id: 'XRF-002688' },
    { event_type: 'manual_merge', changed_by_name: 'Elizabeth Rodriguez', changed_at: 1769302800, reason: 'Steward approval in review queue', source_record_id: 'XRF-000751' },
    { event_type: 'auto_match', changed_by_name: 'System', changed_at: 1769302800, reason: 'System auto-match high confidence', source_record_id: 'XRF-001019' },
    { event_type: 'outlier', changed_by_user_id: 'tu-019', changed_at: 1769302800, reason: 'Corrupted address format', source_record_id: 'XRF-002748' },
    { event_type: 'reject', changed_by_name: 'David Brown', changed_at: 1769216400, reason: 'Confirmed duplicate - already linked as XRF-000152', source_record_id: 'XRF-001102' },
    { event_type: 'threshold_change', changed_by_name: 'Jessica Kim', changed_at: 1769216400, reason: 'Auto-merge threshold updated from 0.85 to 0.90' },
  ];
  return (
    <div className={embedded ? 'audit-embedded' : 'panel audit-panel'}>
      {!embedded && (
        <div className="page-heading compact-heading">
          <div>
            <div className="breadcrumb">Supplier Master › Audit</div>
            <h1>Audit log</h1>
            <p>Trace every match, remap, rejection, outlier decision, and policy change.</p>
          </div>
        </div>
      )}
      <table className="data-table audit-table">
        <thead>
          <tr><th>Event</th><th>Record</th><th>Who</th><th>When</th><th>Reason</th></tr>
        </thead>
        <tbody>
          {rows.map((event, index) => (
            <tr key={index}>
              <td><span className="pill info">{String(event.event_type).replace('_', ' ')}</span></td>
              <td>{event.source_record_id || event.datax_id || '-'}</td>
              <td>{event.changed_by_name || event.changed_by_user_id || 'System'}</td>
              <td>{formatDate(event.changed_at)}</td>
              <td>{event.reason || 'No reason recorded'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationsView({ metrics, connections, activities }: { metrics: DashboardMetrics; connections: ConnectionSummary[]; activities: Activity[] }) {
  const [typeFilter, setTypeFilter] = useState<'all' | 'alert' | 'system' | 'assignment' | 'comment'>('all');
  const [readMap, setReadMap] = useState<Record<string, boolean>>({});
  const sourceRows = activities.length ? activities : fallbackActivities;
  const reviewCount = sourceRows.filter((row) => sourceState(row).label === 'review').length;
  const pendingCount = sourceRows.filter((row) => ['new', 'pending'].includes(sourceState(row).label)).length;
  const riskyConnections = connections.filter((connection) => percentValue(connection.review_records, connection.total_records) > 20 || normalizedTrustScore(connection.trust_score) < 75).length;
  const feed: Array<{ id: string; type: 'alert' | 'system' | 'assignment' | 'comment'; title: string; body: string; created_at: number; cta: string; tone: string; icon: string }> = [
    { id: 'n1', type: 'alert', title: `${reviewCount} records awaiting steward review`, body: 'Prioritize high-confidence matches before they block downstream operations.', created_at: 1780185600, cta: 'Open review queue', tone: 'warning', icon: '!' },
    { id: 'n2', type: 'system', title: `SAP nightly sync completed — ${metrics.inbound_records.toLocaleString()} ingested`, body: `${metrics.auto_merged.toLocaleString()} auto-linked, ${metrics.rejected_outliers.toLocaleString()} excluded as outliers.`, created_at: 1780185600, cta: 'View source records', tone: 'info', icon: '↺' },
    { id: 'n3', type: 'assignment', title: `${pendingCount} records are still pending processing`, body: 'Some rows have not yet been scored or routed into review/new candidate lanes.', created_at: 1780099200, cta: 'Open queue', tone: 'info', icon: 'A' },
    { id: 'n4', type: 'comment', title: 'Comment added on XRF-000504', body: 'Sarah Chen: "Need DUNS enrichment before final review."', created_at: 1780012800, cta: 'Open record', tone: 'success', icon: 'C' },
    { id: 'n5', type: 'alert', title: `${riskyConnections} risky connections need policy tuning`, body: 'High review/failure rates detected on one or more inbound systems.', created_at: 1779926400, cta: 'Open resolution policy', tone: 'warning', icon: '!' },
  ];
  const filtered = feed.filter((item) => typeFilter === 'all' || item.type === typeFilter);
  const groups = {
    Today: filtered.filter((item) => item.created_at >= 1780185600),
    Yesterday: filtered.filter((item) => item.created_at < 1780185600 && item.created_at >= 1780099200),
    Older: filtered.filter((item) => item.created_at < 1780099200),
  };
  return (
    <div className="panel notifications-panel">
      <div className="page-heading compact-heading">
        <div>
          <div className="breadcrumb">Home › Notifications</div>
          <h1>Notifications</h1>
          <p>Backlog, source health, assignments, and comments with direct next actions.</p>
        </div>
      </div>
      <div className="notification-toolbar">
        <div className="status-chip-strip notification-filter-strip">
          {['all', 'alert', 'system', 'assignment', 'comment'].map((type) => (
            <button key={type} className={typeFilter === type ? 'active all' : 'all'} onClick={() => setTypeFilter(type as any)}>
              {type === 'all' ? 'All' : type[0].toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
        <button className="ghost-button" onClick={() => {
          const next: Record<string, boolean> = {};
          feed.forEach((item) => { next[item.id] = true; });
          setReadMap(next);
        }}>Mark all as read</button>
      </div>
      <div className="notification-list">
        {Object.entries(groups).map(([label, items]) => (
          items.length ? (
            <section key={label} className="notification-group">
              <h3>{label}</h3>
              {items.map((item) => (
                <article className={`notification-card ${item.tone}`} key={item.id}>
                  <div className="notification-leading">
                    <span className={`notification-icon ${item.tone}`}>{item.icon}</span>
                  </div>
                  <div className="notification-content">
                    <div className="notification-meta">
                      <span className="pill info">{item.type}</span>
                      {!readMap[item.id] && <span className="unread-dot" />}
                    </div>
                    <strong>{item.title}</strong>
                    <p>{item.body}</p>
                    <button className={`secondary-button ${item.tone === 'warning' ? 'severity-warning' : 'severity-secondary'}`} onClick={() => setReadMap((prev) => ({ ...prev, [item.id]: true }))}>{item.cta}</button>
                  </div>
                  <div className="notification-trailing">
                    <span>{formatDate(item.created_at)}</span>
                    <button className="ghost-button" onClick={() => setReadMap((prev) => ({ ...prev, [item.id]: true }))}>Dismiss</button>
                  </div>
                </article>
              ))}
            </section>
          ) : null
        ))}
      </div>
    </div>
  );
}

function MergeWizard({ step, setStep, source, selected, target, suppliers, targetId, setTargetId, signals, onClose, onDone }: any) {
  return (
    <div className="wizard-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="wizard-page">
        <WizardHeader title="Resolve record" step={step} labels={['Select target', 'Choose action', 'Review fields', 'Confirm']} onClose={onClose} />
        {step === 1 && (
          <div className="wizard-section side-by-side">
            <SupplierMiniCard supplier={selected} label="Source supplier" />
            <div className="target-box">
              <h3>Select target golden supplier record</h3>
              <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                {suppliers.map((supplier: Supplier) => <option key={supplier.datax_id} value={supplier.datax_id}>{supplier.master_legal_name}</option>)}
              </select>
            </div>
          </div>
        )}
        {step === 2 && (
          <>
            <div className="wizard-section">
              <h2>Choose resolution action</h2>
              <div className="strategy active">Approve link to existing GSR: {target.master_legal_name}<p>{selected.master_legal_name} source records will map into this canonical record.</p></div>
              <div className="strategy">Create new GSR candidate<p>Use when no existing canonical supplier is correct.</p></div>
            </div>
            <div className="ai-panel wizard-ai">
              <strong>Match confidence: {fmtScore(source?.match_score || 0.91)}</strong>
              <p>Signal summary used by resolution policy and steward review.</p>
              {signals.map((signal: MatchSignal) => <Signal key={signal.label} signal={signal} />)}
            </div>
          </>
        )}
        {step === 3 && <CompareEdit selected={selected} target={target} />}
        {step === 4 && (
          <div className="wizard-section">
            <h2>Confirm resolution</h2>
            <p>This decision writes to audit log and updates source lineage immediately.</p>
            <textarea placeholder="Reason for resolution">Steward confirmed supplier identity after reviewing match signals.</textarea>
          </div>
        )}
        <WizardFooter step={step} setStep={setStep} onClose={onClose} onDone={onDone} doneLabel="Confirm resolution" />
      </div>
    </div>
  );
}

function SplitWizard({ step, setStep, sourceRecords, onClose, onDone }: any) {
  return (
    <div className="wizard-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="wizard-page">
        <WizardHeader title="Split supplier" step={step} labels={['Choose records', 'Create supplier', 'Review mapping', 'Confirm']} onClose={onClose} />
        <div className="wizard-section">
          <div className="strategy active">Keep current supplier as canonical<p>Selected source records will move to a new golden supplier record.</p></div>
          <div className="source-grid">
            {sourceRecords.map((record: SourceRecord) => (
              <div className="source-card" key={record.source_record_id}>
                <strong>{record.source_supplier_name}</strong>
                <p>{record.source_name} • {fmtScore(record.match_score)} confidence</p>
                <button className="secondary-button" onClick={() => {
                  setStep(2);
                  announceAction(`${record.source_supplier_name} selected for new GSR creation.`);
                }}>Create new GSR</button>
              </div>
            ))}
          </div>
        </div>
        <WizardFooter step={step} setStep={setStep} onClose={onClose} onDone={onDone} doneLabel="Confirm split" />
      </div>
    </div>
  );
}

function WizardHeader({ title, step, labels, onClose }: any) {
  return (
    <>
      <div className="wizard-title"><span className="breadcrumb">Supplier Master › {title}</span><button onClick={onClose}>×</button></div>
      <div className="stepper">
        {labels.map((label: string, index: number) => (
          <div key={label} className={step >= index + 1 ? 'active' : ''}><span>{index + 1}</span>{label}</div>
        ))}
      </div>
    </>
  );
}

function WizardFooter({ step, setStep, onClose, onDone, doneLabel }: any) {
  return (
    <div className="wizard-footer">
      <button className="secondary-button" onClick={step === 1 ? onClose : () => setStep((step - 1) as WizardStep)}>{step === 1 ? 'Cancel' : 'Back'}</button>
      <button className="primary-button" onClick={step === 4 ? onDone : () => setStep((step + 1) as WizardStep)}>{step === 4 ? doneLabel : 'Continue'}</button>
    </div>
  );
}

function CompareEdit({ selected, target }: { selected: Supplier; target: Supplier }) {
  const fields = [
    ['Supplier ID', selected.tenant_supplier_code, target.tenant_supplier_code],
    ['DUNS ID', selected.master_duns_number, target.master_duns_number],
    ['Tax ID', selected.master_tax_id, target.master_tax_id],
    ['Classification', selected.tenant_classification, target.tenant_classification],
    ['Industry', 'Food & Beverage Distribution', 'Foodservice'],
    ['Website', selected.master_website, target.master_website],
  ];
  return (
    <div className="compare-layout">
      <div className="compare-column">
        <h2>Source Supplier - {selected.master_legal_name}</h2>
        {fields.map(([label, value]) => <CompareField key={label} label={label} value={value} />)}
      </div>
      <div className="compare-column">
        <h2>Target Supplier - {target.master_legal_name}</h2>
        {fields.map(([label, , value]) => <CompareField key={label} label={label} value={value} selected />)}
      </div>
      <div className="compare-column output">
        <h2>New Golden Supplier Record</h2>
        {fields.map(([label, , value]) => <CompareField key={label} label={label} value={value} selected custom />)}
      </div>
    </div>
  );
}

function CompareField({ label, value, selected = false, custom = false }: any) {
  return (
    <div className={`compare-field ${selected ? 'selected' : ''}`}>
      <span className="radio">{selected ? '●' : '○'}</span>
      <strong>{label}</strong>
      {custom && <span className="custom-chip">Custom</span>}
      <p>{value || '-'}</p>
    </div>
  );
}

function SupplierMiniCard({ supplier, label }: { supplier: Supplier; label: string }) {
  return (
    <div className="mini-card">
      <Logo supplier={supplier} />
      <div>
        <h2>{supplier.master_legal_name} <span>({supplier.tenant_supplier_code})</span></h2>
        <span className="pill gray">{label}</span>
        <p>DUNS ID: {supplier.master_duns_number}</p>
        <p>Location: {supplier.city}, {supplier.country}</p>
        <p>Industry: Food & Beverage</p>
      </div>
    </div>
  );
}

function Signal({ signal }: { signal: MatchSignal }) {
  return (
    <div className="signal">
      <span>{signal.label}</span>
      <div><i style={{ width: `${signal.score * 100}%` }} /></div>
      <strong>{Math.round(signal.score * 100)}%</strong>
      <p>{signal.detail}</p>
    </div>
  );
}

function Meta({ label, value, link = false }: { label: string; value?: string; link?: boolean }) {
  if (!link) return <div className="meta"><span>{label}</span><strong>{value || '-'}</strong></div>;
  const normalized = value?.startsWith('http') ? value : `https://${value || ''}`;
  return (
    <div className="meta">
      <span>{label}</span>
      {value ? <a href={normalized} target="_blank" rel="noreferrer">{value}</a> : <strong>-</strong>}
    </div>
  );
}

function Logo({ supplier, large = false }: { supplier: Partial<Supplier>; large?: boolean }) {
  return (
    <div className={`logo-box ${large ? 'large' : ''}`}>
      {supplier.logo_url ? <img src={supplier.logo_url} alt="" /> : <span>{supplier.master_legal_name?.slice(0, 2).toUpperCase()}</span>}
    </div>
  );
}

function LogoSlot({ supplier, logoUrl, onUpload }: { supplier: Supplier; logoUrl?: string; onUpload: (dataUrl: string) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  async function fileToDataUrl(file: File) {
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    onUpload(dataUrl);
  }
  return (
    <div
      className="logo-box large logo-slot"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && /image\/(png|jpeg|jpg|webp)/.test(file.type)) fileToDataUrl(file);
      }}
      title="Drop PNG/JPG logo or click to upload"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) fileToDataUrl(file);
        }}
      />
      {logoUrl ? <img src={logoUrl} alt={`${supplier.master_legal_name} logo`} /> : <span>{supplier.master_legal_name?.slice(0, 2).toUpperCase()}</span>}
    </div>
  );
}

function SettingsView() {
  const [tab, setTab] = useState<'source' | 'enrichment'>('source');
  const [showAddConnection, setShowAddConnection] = useState(false);
  const [showConnect, setShowConnect] = useState<string | null>(null);
  const [providers, setProviders] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [connections, setConnections] = useState<any[]>([
    { name: 'ERP-SAP', systemType: 'ERP', method: 'API', lastSync: 'May 30, 2026 01:10', records: 900, trust: 95, status: 'active' },
    { name: 'ERP-Oracle', systemType: 'ERP', method: 'SFTP', lastSync: 'May 30, 2026 02:22', records: 600, trust: 95, status: 'active' },
    { name: 'Vendor Portal', systemType: 'Vendor portal', method: 'API', lastSync: 'May 29, 2026 21:00', records: 600, trust: 75, status: 'active' },
    { name: 'AP Legacy', systemType: 'AP/Finance', method: 'Manual', lastSync: 'May 28, 2026 11:10', records: 450, trust: 60, status: 'warning' },
    { name: 'Procurement', systemType: 'Procurement', method: 'Email', lastSync: 'May 30, 2026 03:45', records: 450, trust: 72, status: 'active' },
  ]);
  const enrichment = [
    { name: 'D&B', status: 'disconnected', lastSync: '—', enriched: 0 },
    { name: 'OpenCorporates', status: 'connected', lastSync: 'May 30, 2026', enriched: 620 },
    { name: 'Companies House', status: 'disconnected', lastSync: '—', enriched: 0 },
    { name: 'GLEIF', status: 'connected', lastSync: 'May 29, 2026', enriched: 418 },
    { name: 'SEC EDGAR', status: 'disconnected', lastSync: '—', enriched: 0 },
  ];
  useEffect(() => {
    api<any>(`/api/providers?tenant_id=${TENANT_ID}`).then((data) => setProviders(data.providers || [])).catch(() => undefined);
    api<any>(`/api/providers/runs?tenant_id=${TENANT_ID}`).then((data) => setRuns(data.runs || [])).catch(() => undefined);
  }, []);
  const providerRows = providers.length ? providers : enrichment.map((item) => ({
    provider_id: item.name,
    provider_name: item.name,
    status: item.status,
    rights_status: item.name === 'OpenCorporates' ? 'pending' : item.name === 'D&B' ? 'pending' : 'approved',
    last_sync_at: null,
  }));
  const activeCount = connections.filter((c) => c.status === 'active').length;
  const warningCount = connections.filter((c) => c.status !== 'active').length;
  const totalRecords = connections.reduce((sum, c) => sum + c.records, 0);
  return (
    <>
      <div className="page-heading">
        <div><div className="breadcrumb">Home › Settings</div><h1>Settings</h1><p>Manage operational source connections and enrichment providers.</p></div>
      </div>
      <section className="panel">
        <div className="tabs profile-tabs">
          <button className={tab === 'source' ? 'active' : ''} onClick={() => setTab('source')}>Source connections</button>
          <button className={tab === 'enrichment' ? 'active' : ''} onClick={() => setTab('enrichment')}>Enrichment sources</button>
        </div>
        {tab === 'source' && (
          <div className="profile-section">
            <div className="profile-card-grid">
              <article className="info-card identity-card"><span className="card-kicker">Total connections</span><strong>{connections.length}</strong></article>
              <article className="info-card identity-card"><span className="card-kicker">Active</span><strong>{activeCount}</strong></article>
              <article className="info-card identity-card"><span className="card-kicker">Warnings</span><strong>{warningCount}</strong></article>
              <article className="info-card identity-card"><span className="card-kicker">Records ingested</span><strong>{totalRecords.toLocaleString()}</strong></article>
            </div>
            <div className="section-toolbar"><h2>Operational sources</h2><button className="primary-button" onClick={() => setShowAddConnection(true)}>Add connection</button></div>
            <table className="data-table compact-table"><thead><tr><th>Connection</th><th>System</th><th>Method</th><th>Last sync</th><th>Records</th><th>Trust</th><th>Status</th></tr></thead><tbody>
              {connections.map((c) => <tr key={c.name}><td>{c.name}</td><td>{c.systemType}</td><td><span className="pill gray">{c.method}</span></td><td>{c.lastSync}</td><td>{c.records.toLocaleString()}</td><td><div className="dq-mini good"><i style={{ width: `${c.trust}%` }} /></div></td><td><span className={`pill ${c.status === 'active' ? 'success' : 'warning'}`}>{c.status}</span></td></tr>)}
            </tbody></table>
          </div>
        )}
        {tab === 'enrichment' && (
          <div className="profile-section">
            <table className="data-table compact-table"><thead><tr><th>Provider</th><th>Status</th><th>Last sync</th><th>Records enriched</th><th>Action</th></tr></thead><tbody>
              {providerRows.map((s) => <tr key={s.provider_id || s.provider_name}><td>{s.provider_name}</td><td><span className={`pill ${s.status === 'connected' ? 'success' : 'gray'}`}>{s.status}</span></td><td>{s.last_sync_at ? formatDate(s.last_sync_at) : '—'}</td><td><span className={`pill ${s.rights_status === 'approved' ? 'success' : 'warning'}`}>{s.rights_status}</span></td><td><button className="secondary-button severity-secondary" onClick={() => setShowConnect(s.provider_name)}>{s.status === 'connected' ? 'Edit' : 'Connect'}</button></td></tr>)}
            </tbody></table>
            <div className="section-toolbar"><h2>Runs monitor</h2><span>{runs.length} recent jobs</span></div>
            <table className="data-table compact-table"><thead><tr><th>Run</th><th>Type</th><th>Status</th><th>Provider</th><th>Updated</th></tr></thead><tbody>
              {(runs.length ? runs : [{ run_id: 'run-placeholder', run_type: 'search', status: 'queued', provider_name: 'Provider jobs appear here', updated_at: null }]).map((run) => <tr key={run.run_id}><td>{run.run_id}</td><td>{run.run_type}</td><td><span className={`pill ${run.status === 'succeeded' ? 'success' : run.status === 'failed' ? 'danger' : 'gray'}`}>{run.status}</span></td><td>{run.provider_name || '-'}</td><td>{run.updated_at ? formatDate(run.updated_at) : '-'}</td></tr>)}
            </tbody></table>
          </div>
        )}
      </section>
      {showAddConnection && <AddConnectionWizard onClose={() => setShowAddConnection(false)} onSave={(row) => { setConnections((prev) => [...prev, row]); setShowAddConnection(false); }} />}
      {showConnect && <ConnectSourceModal source={showConnect} onClose={() => setShowConnect(null)} />}
    </>
  );
}

function ConnectSourceModal({ source, onClose }: { source: string; onClose: () => void }) {
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [tested, setTested] = useState(false);
  return (
    <div className="modal-overlay gsr-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-card">
        <div className="modal-header"><h2>Connect {source}</h2><button className="icon-button modal-close" onClick={onClose}>×</button></div>
        <div className="modal-grid one-col">
          <label>API key<input value={apiKey} onChange={(e) => setApiKey(e.target.value)} /></label>
          <label>API secret<input value={secret} onChange={(e) => setSecret(e.target.value)} /></label>
          <button className="secondary-button severity-secondary" onClick={() => setTested(true)}>Test connection</button>
          {tested && <p>Connection test successful.</p>}
        </div>
        <div className="modal-footer"><button className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" onClick={onClose}>Save</button></div>
      </div>
    </div>
  );
}

function AddConnectionWizard({ onClose, onSave }: { onClose: () => void; onSave: (row: any) => void }) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [name, setName] = useState('');
  const [systemType, setSystemType] = useState('ERP');
  const [systemTypeOther, setSystemTypeOther] = useState('');
  const [description, setDescription] = useState('');
  const [method, setMethod] = useState<'API' | 'SFTP' | 'File upload' | 'Email' | 'Manual'>('API');
  const [schedule, setSchedule] = useState('Daily');
  const [trust, setTrust] = useState(75);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [authType, setAuthType] = useState('Bearer token');
  const [sftpHost, setSftpHost] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  return (
    <div className="modal-overlay gsr-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-card">
        <div className="modal-header"><h2>Add connection</h2><button className="icon-button modal-close" onClick={onClose}>×</button></div>
        <div className="modal-grid one-col">
          <div className="upload-steps"><span className={step === 1 ? 'active' : ''}>1. Identity</span><span className={step === 2 ? 'active' : ''}>2. Method</span><span className={step === 3 ? 'active' : ''}>3. Schedule</span><span className={step === 4 ? 'active' : ''}>4. Review</span></div>
          {step === 1 && (<><label>Name<input value={name} onChange={(e) => setName(e.target.value)} /></label><label>System type<select value={systemType} onChange={(e) => setSystemType(e.target.value)}><option>ERP</option><option>CRM</option><option>AP/Finance</option><option>Procurement</option><option>Vendor portal</option><option>HR</option><option>Legacy</option><option>Other</option></select></label><label>Description<input value={description} onChange={(e) => setDescription(e.target.value)} /></label></>)}
          {step === 1 && systemType === 'Other' && <label>Other system type<input value={systemTypeOther} onChange={(e) => setSystemTypeOther(e.target.value)} /></label>}
          {step === 2 && (<><label>Connection method<select value={method} onChange={(e) => setMethod(e.target.value as any)}><option>API</option><option>SFTP</option><option>File upload</option><option>Email</option><option>Manual</option></select></label>
            {method === 'API' && (<><label>Base URL<input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} /></label><label>API key<input value={apiKey} onChange={(e) => setApiKey(e.target.value)} /></label><label>API secret<input value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} /></label><label>Auth type<select value={authType} onChange={(e) => setAuthType(e.target.value)}><option>Bearer token</option><option>Basic auth</option><option>OAuth2 client credentials</option><option>API key header</option><option>API key query param</option></select></label></>)}
            {method === 'SFTP' && (<><label>Host<input value={sftpHost} onChange={(e) => setSftpHost(e.target.value)} /></label><label>Port<input defaultValue={22} /></label><label>Username<input /></label><label>Password / SSH key<input /></label><label>Remote directory<input /></label><label>File pattern<input /></label><label>Format<select><option>CSV</option><option>Excel</option><option>JSON</option><option>XML</option></select></label></>)}
            {method === 'File upload' && (<><label>Format<select><option>CSV</option><option>Excel</option><option>JSON</option><option>XML</option><option>pipe</option><option>tab</option></select></label><label>Delimiter<input defaultValue="," /></label><label>Encoding<input defaultValue="UTF-8" /></label></>)}
            {method === 'Email' && (<><label>Monitored address<input value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} /></label><label>IMAP server<input /></label><label>Subject filter<input /></label><label>Attachment format<select><option>CSV</option><option>Excel</option><option>JSON</option></select></label></>)}
            {method === 'Manual' && <p>No credentials required for manual connections.</p>}
          </>)}
          {step === 3 && (<><label>Sync schedule<select value={schedule} onChange={(e) => setSchedule(e.target.value)}><option>Real-time webhook</option><option>Hourly</option><option>Daily</option><option>Weekly</option><option>Manual trigger</option></select></label><label>Trust score {trust}%<input type="range" min={0} max={100} value={trust} onChange={(e) => setTrust(Number(e.target.value))} /></label><div className="button-row"><button className="secondary-button" onClick={() => setTrust(90)}>ERP / master data (90%)</button><button className="secondary-button" onClick={() => setTrust(75)}>Vendor self-service (75%)</button><button className="secondary-button" onClick={() => setTrust(60)}>Manual / legacy (60%)</button></div></>)}
          {step === 4 && <p>{name || 'Connection'} · {(systemType === 'Other' ? (systemTypeOther || 'Other') : systemType)} · {method} · {schedule} · trust {trust}%{method === 'API' ? ` · ${authType}` : ''}</p>}
        </div>
        <div className="modal-footer">
          {step > 1 && <button className="secondary-button" onClick={() => setStep((step - 1) as any)}>Back</button>}
          {step < 4 && <button className="primary-button" onClick={() => setStep((step + 1) as any)}>Next</button>}
          {step === 4 && <button className="primary-button" onClick={() => onSave({ name: name || 'New connection', systemType: systemType === 'Other' ? (systemTypeOther || 'Other') : systemType, method, lastSync: 'Just now', records: 0, trust, status: 'active' })}>Confirm</button>}
        </div>
      </div>
    </div>
  );
}

function NavIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

function EnrichGlyph({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" fill={color} />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" fill={color} opacity={0.72} />
    </svg>
  );
}

export default App;

function SignedOutScreen({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="signed-out">
      <div className="signed-out-shell">
        <aside className="signed-out-aside">
          <img src="/assets/logo/dataX.svg" alt="dataX.ai" className="signed-out-logo" />
          <h1>Supplier Master</h1>
          <p>Entity resolution, stewardship, and match-merge workflow.</p>
          <div className="domain-badges">
            <span className="pill gray">Allowed: @datax.ai</span>
            <span className="pill gray">Allowed: @crowdanalytix.com</span>
          </div>
        </aside>
        <section className="signed-out-card">
          <h2>Sign in</h2>
          <p>Continue with your Google Workspace account.</p>
          <button className="primary-button login-google" onClick={onLogin}>
            <span>G</span> Sign in with Google
          </button>
          <small>Use your corporate domain email. Unauthorized domains are blocked.</small>
        </section>
      </div>
    </div>
  );
}

function SupplierFormModal({
  title,
  submitLabel,
  mode,
  seed,
  onClose,
  onSubmit,
  initialValues,
}: {
  title: string;
  submitLabel: string;
  mode: 'create' | 'edit';
  seed?: GsrSeed;
  onClose: () => void;
  onSubmit: (form: {
    master_legal_name: string;
    tenant_supplier_code: string;
    master_tax_id?: string;
    master_duns_number?: string;
    master_website?: string;
    tenant_classification: 'strategic' | 'preferred' | 'tactical';
    tenant_status?: 'active' | 'inactive' | 'blocked';
    city?: string;
    state_province?: string;
    country?: string;
    source_xrefs?: number;
    data_quality_score?: number;
    active_certs?: number;
    esg_rating?: string;
    esg_score?: number;
    risk_level?: string;
    risk_score?: number;
    diversity_type?: string;
    diversity_status?: string;
    sanctions_status?: string;
    sanctions_provider?: string;
    doc_1_name?: string;
    doc_1_type?: string;
    doc_1_url?: string;
    doc_2_name?: string;
    doc_2_type?: string;
    doc_2_url?: string;
    doc_notes?: string;
  }) => Promise<void>;
  initialValues?: Partial<{
    master_legal_name: string;
    tenant_supplier_code: string;
    master_tax_id: string;
    master_duns_number: string;
    master_website: string;
    tenant_classification: 'strategic' | 'preferred' | 'tactical';
    tenant_status?: 'active' | 'inactive' | 'blocked';
    city: string;
    state_province: string;
    country: string;
  }>;
}) {
  const [section, setSection] = useState<'core' | 'identifiers' | 'related' | 'agreements' | 'documents' | 'notes'>('core');
  const [form, setForm] = useState({
    master_legal_name: initialValues?.master_legal_name || seed?.source_supplier_name || '',
    tenant_supplier_code: initialValues?.tenant_supplier_code || '',
    master_duns_number: initialValues?.master_duns_number || '',
    master_tax_id: initialValues?.master_tax_id || '',
    master_website: initialValues?.master_website || '',
    tenant_classification: initialValues?.tenant_classification || 'preferred',
    tenant_status: initialValues?.tenant_status || 'active',
    city: initialValues?.city || '',
    state_province: initialValues?.state_province || '',
    country: initialValues?.country || 'US',
    preferred: mode === 'edit',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    zip: '',
    source_xrefs: 0,
    data_quality_score: 0,
    active_certs: 0,
    esg_rating: 'Unrated',
    esg_score: 0,
    risk_level: 'low',
    risk_score: 0,
    diversity_type: '',
    diversity_type_other: '',
    diversity_status: '',
    diversity_status_other: '',
    sanctions_status: 'clear',
    sanctions_provider: 'Dow Jones',
    sanctions_provider_other: '',
    doc_1_name: '',
    doc_1_type: 'Certificate',
    doc_1_url: '',
    doc_2_name: '',
    doc_2_type: 'Insurance',
    doc_2_url: '',
    doc_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const isValid = form.master_legal_name.trim().length >= 2;

  return (
    <div className="modal-overlay gsr-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-card gsr-modal">
        <div className="modal-header">
          <div>
            <h2>{title}</h2>
            <p>{mode === 'create' ? `Prefilled from ${seed?.source_record_id || 'source record'}${seed?.source_supplier_name ? ` · ${seed.source_supplier_name}` : ''}` : (seed?.candidate_datax_id || '')}</p>
          </div>
          <button className="icon-button modal-close" onClick={onClose}>×</button>
        </div>
        <div className="gsr-modal-layout">
          <aside className="gsr-sidebar">
            {[
              ['core', 'Core'],
              ['identifiers', 'Identifiers'],
              ['related', 'Related entities'],
              ['agreements', 'Agreements'],
              ['documents', 'Documents'],
              ['notes', 'Notes'],
            ].map(([id, label]) => (
              <button key={id} className={section === id ? 'active' : ''} onClick={() => setSection(id as any)}>{label}</button>
            ))}
          </aside>
          <div className="modal-grid">
            {section === 'core' && (
              <>
                <label>Legal name<input value={form.master_legal_name} onChange={(e) => setForm({ ...form, master_legal_name: e.target.value })} /></label>
                <label>Supplier code<input value={form.tenant_supplier_code} onChange={(e) => setForm({ ...form, tenant_supplier_code: e.target.value })} /></label>
                <label>Record status
                  <select value={form.tenant_status} onChange={(e) => setForm({ ...form, tenant_status: e.target.value as 'active' | 'inactive' | 'blocked' })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </label>
                <label>Classification
                  <select value={form.tenant_classification} onChange={(e) => setForm({ ...form, tenant_classification: e.target.value as 'strategic' | 'preferred' | 'tactical' })}>
                    <option value="strategic">Strategic</option>
                    <option value="preferred">Preferred</option>
                    <option value="tactical">Tactical</option>
                  </select>
                </label>
                <label>Primary contact name<input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></label>
                <label>Email<input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></label>
                <label>Phone<input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></label>
              </>
            )}
            {section === 'identifiers' && (
              <>
                <div className="info-card identity-card" style={{ gridColumn: '1 / -1', padding: '12px 14px', marginBottom: 2, background: '#f8fafc' }}>
                  <span className="card-kicker">System-owned (read-only)</span>
                  <strong style={{ marginBottom: 0 }}>{form.tenant_supplier_code || '—'}</strong>
                  <p style={{ marginBottom: 0 }}>Client master ID</p>
                </div>
                <label>D&B DUNS<input value={form.master_duns_number} onChange={(e) => setForm({ ...form, master_duns_number: e.target.value })} /></label>
                <label>Tax ID<input value={form.master_tax_id} onChange={(e) => setForm({ ...form, master_tax_id: e.target.value })} /></label>
                <label className="full-span">Website<input value={form.master_website} onChange={(e) => setForm({ ...form, master_website: e.target.value })} /></label>
              </>
            )}
            {section === 'related' && (
              <>
                <label>HQ city<input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
                <label>HQ state / province<input value={form.state_province} onChange={(e) => setForm({ ...form, state_province: e.target.value })} /></label>
                <label>HQ country<input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></label>
                <label className="full-span">Related entities are managed from the entity record screen. Use the record view to link parents, subsidiaries, and affiliates.</label>
              </>
            )}
            {section === 'agreements' && (
              <>
                <label>Linked source records<input type="number" min={0} value={form.source_xrefs} onChange={(e) => setForm({ ...form, source_xrefs: Number(e.target.value) || 0 })} /></label>
                <label>Data quality score<input type="number" min={0} max={100} value={form.data_quality_score} onChange={(e) => setForm({ ...form, data_quality_score: Number(e.target.value) || 0 })} /></label>
                <label>Active certifications<input type="number" min={0} value={form.active_certs} onChange={(e) => setForm({ ...form, active_certs: Number(e.target.value) || 0 })} /></label>
                <label>ESG rating
                  <select value={form.esg_rating} onChange={(e) => setForm({ ...form, esg_rating: e.target.value })}>
                    <option>Gold</option><option>Silver</option><option>Bronze</option><option>Unrated</option>
                  </select>
                </label>
                <label>ESG score<input type="number" min={0} max={100} value={form.esg_score} onChange={(e) => setForm({ ...form, esg_score: Number(e.target.value) || 0 })} /></label>
                <label>Risk level
                  <select value={form.risk_level} onChange={(e) => setForm({ ...form, risk_level: e.target.value })}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </label>
                <label>Risk score<input type="number" min={0} max={100} value={form.risk_score} onChange={(e) => setForm({ ...form, risk_score: Number(e.target.value) || 0 })} /></label>
                <label>Diversity type
                  <select value={form.diversity_type} onChange={(e) => setForm({ ...form, diversity_type: e.target.value })}>
                    <option value="">Select type</option>
                    <option value="WBE">WBE</option><option value="MBE">MBE</option><option value="SBE">SBE</option><option value="VBE">VBE</option><option value="LGBTBE">LGBTBE</option><option value="Other">Other</option>
                  </select>
                </label>
                {form.diversity_type === 'Other' && <label>Other diversity type<input value={form.diversity_type_other} onChange={(e) => setForm({ ...form, diversity_type_other: e.target.value })} /></label>}
                <label>Diversity status
                  <select value={form.diversity_status} onChange={(e) => setForm({ ...form, diversity_status: e.target.value })}>
                    <option value="">Select status</option>
                    <option value="verified">Verified</option><option value="pending">Pending</option><option value="expired">Expired</option><option value="self_certified">Self-certified</option><option value="Other">Other</option>
                  </select>
                </label>
                {form.diversity_status === 'Other' && <label>Other diversity status<input value={form.diversity_status_other} onChange={(e) => setForm({ ...form, diversity_status_other: e.target.value })} /></label>}
                <label>Sanctions status
                  <select value={form.sanctions_status} onChange={(e) => setForm({ ...form, sanctions_status: e.target.value })}>
                    <option value="clear">Clear</option><option value="watch">Watch</option><option value="match">Match</option>
                  </select>
                </label>
                <label>Sanctions provider
                  <select value={form.sanctions_provider} onChange={(e) => setForm({ ...form, sanctions_provider: e.target.value })}>
                    <option value="Dow Jones">Dow Jones</option><option value="LexisNexis">LexisNexis</option><option value="Refinitiv">Refinitiv</option><option value="Other">Other</option>
                  </select>
                </label>
                {form.sanctions_provider === 'Other' && <label>Other sanctions provider<input value={form.sanctions_provider_other} onChange={(e) => setForm({ ...form, sanctions_provider_other: e.target.value })} /></label>}
                <div className="preferred-toggle">
                  <span>Preferred supplier</span>
                  <button className={form.preferred ? 'on' : ''} onClick={() => setForm({ ...form, preferred: !form.preferred })}><i /></button>
                </div>
              </>
            )}
            {section === 'documents' && (
              <>
                <label>Document 1 name<input value={form.doc_1_name} onChange={(e) => setForm({ ...form, doc_1_name: e.target.value })} placeholder="W-9 FY2026" /></label>
                <label>Document 1 type
                  <select value={form.doc_1_type} onChange={(e) => setForm({ ...form, doc_1_type: e.target.value })}>
                    <option>Certificate</option><option>Insurance</option><option>Compliance</option><option>Legal</option><option>Tax</option><option>Other</option>
                  </select>
                </label>
                <label>Document 1 URL/path<input value={form.doc_1_url} onChange={(e) => setForm({ ...form, doc_1_url: e.target.value })} placeholder="https://... or /documents/..." /></label>
                <label>Document 2 name<input value={form.doc_2_name} onChange={(e) => setForm({ ...form, doc_2_name: e.target.value })} placeholder="COI 2026" /></label>
                <label>Document 2 type
                  <select value={form.doc_2_type} onChange={(e) => setForm({ ...form, doc_2_type: e.target.value })}>
                    <option>Insurance</option><option>Certificate</option><option>Compliance</option><option>Legal</option><option>Tax</option><option>Other</option>
                  </select>
                </label>
                <label>Document 2 URL/path<input value={form.doc_2_url} onChange={(e) => setForm({ ...form, doc_2_url: e.target.value })} placeholder="https://... or /documents/..." /></label>
                <label className="full-span">Document notes<textarea value={form.doc_notes} onChange={(e) => setForm({ ...form, doc_notes: e.target.value })} placeholder="Notes on expiry, verification, owners, and review cadence." /></label>
              </>
            )}
            {section === 'notes' && (
              <>
                <label>Address<input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
                <label>ZIP<input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></label>
                <label className="full-span">Use this area for internal context, audit notes, or change reasons. Anything saved here remains tenant-scoped.</label>
              </>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <span className="form-hint">{mode === 'create' ? 'Legal name must be at least 2 characters.' : `Editing ${seed?.candidate_datax_id || ''}`}</span>
          <button className="secondary-button" onClick={onClose}>Cancel</button>
          <button
            className="primary-button"
            disabled={saving || !isValid}
            onClick={async () => {
              if (!isValid) return;
              setSaving(true);
              await onSubmit({
                master_legal_name: form.master_legal_name,
                tenant_supplier_code: form.tenant_supplier_code,
                master_tax_id: form.master_tax_id || undefined,
                master_duns_number: form.master_duns_number || undefined,
                master_website: form.master_website || undefined,
                tenant_classification: form.tenant_classification,
                city: form.city || undefined,
                state_province: form.state_province || undefined,
                country: form.country || undefined,
                source_xrefs: form.source_xrefs,
                data_quality_score: form.data_quality_score,
                active_certs: form.active_certs,
                esg_rating: form.esg_rating,
                esg_score: form.esg_score,
                risk_level: form.risk_level,
                risk_score: form.risk_score,
                diversity_type: (form.diversity_type === 'Other' ? form.diversity_type_other : form.diversity_type) || undefined,
                diversity_status: (form.diversity_status === 'Other' ? form.diversity_status_other : form.diversity_status) || undefined,
                sanctions_status: form.sanctions_status,
                sanctions_provider: (form.sanctions_provider === 'Other' ? form.sanctions_provider_other : form.sanctions_provider) || undefined,
                doc_1_name: form.doc_1_name || undefined,
                doc_1_type: form.doc_1_type || undefined,
                doc_1_url: form.doc_1_url || undefined,
                doc_2_name: form.doc_2_name || undefined,
                doc_2_type: form.doc_2_type || undefined,
                doc_2_url: form.doc_2_url || undefined,
                doc_notes: form.doc_notes || undefined,
              });
              setSaving(false);
            }}
          >
            {saving ? 'Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function CsvUploadGuideModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: (summary: { inserted: number; updated: number; failed: number }) => Promise<void>;
}) {
  const sample = `source_record_id,tenant_id,source_system_id,source_name,source_supplier_name,source_tax_id,source_duns,source_website,source_address,source_city,source_state,source_postal_code,source_country,source_category,record_status
XRF-100001,tenant-hospitality-demo,SAP01,ERP-SAP,Northwind Foods LLC,12-3456789,01-234-5678,northwindfoods.com,100 Main St,Dallas,TX,75201,US,Food Distribution,new
XRF-100002,tenant-hospitality-demo,ORCL01,ERP-Oracle,Blue Harbor Produce,98-7654321,98-765-4321,blueharborproduce.com,250 Harbor Blvd,Miami,FL,33101,US,Produce,review
XRF-100003,tenant-hospitality-demo,VPORT01,Vendor Portal,Peak Linen Supply,,12-987-6543,peaklinen.com,42 Ridge Ave,Denver,CO,80202,US,Linens,matched`;
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [connection, setConnection] = useState('ERP-SAP');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [errorCsvHref, setErrorCsvHref] = useState('');

  return (
    <div className="modal-overlay gsr-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-card">
        <div className="modal-header">
          <h2>Upload CSV Batch</h2>
          <button className="icon-button modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-grid one-col">
          <div className="upload-steps"><span className={step === 1 ? 'active' : ''}>1. Configure</span><span className={step === 2 ? 'active' : ''}>2. Upload</span><span className={step === 3 ? 'active' : ''}>3. Done</span></div>
          {step === 1 && (
            <>
              <label>Target connection
                <select value={connection} onChange={(e) => setConnection(e.target.value)}>
                  <option>ERP-SAP</option><option>ERP-Oracle</option><option>Vendor Portal</option><option>AP Legacy</option><option>Procurement</option>
                </select>
              </label>
              <p>Required columns:</p>
              <code>source_record_id, tenant_id, source_system_id, source_name, source_supplier_name, source_country, record_status</code>
              <p>Optional columns:</p>
              <code>source_tax_id, source_duns, source_website, source_address, source_city, source_state, source_postal_code, source_category</code>
              <a className="secondary-button severity-secondary" href={`data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`} download="supplier_source_record_sample.csv">Download sample CSV</a>
            </>
          )}
          {step === 2 && (
            <>
              <p>Target connection: <strong>{connection}</strong></p>
              <div className="upload-controls dropzone">
                <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>
              <button
                className="primary-button"
                disabled={!file || uploading}
                onClick={async () => {
                  if (!file) return;
                  setUploading(true);
                  setResult('');
                  setErrorCsvHref('');
                  try {
                    const form = new FormData();
                    form.append('file', file);
                    form.append('tenant_id', TENANT_ID);
                    const response = await api<any>('/api/match-merge/suppliers/upload-csv', { method: 'POST', body: form });
                    setResult(`Uploaded ${response.total_rows} rows. Inserted: ${response.inserted}, Updated: ${response.updated}, Failed: ${response.failed}`);
                    if (Array.isArray(response.errors) && response.errors.length > 0) {
                      const errorCsv = ['row,source_record_id,error', ...response.errors.map((e: any) => `${e?.row ?? ''},"${String(e?.source_record_id ?? '').split('"').join('""')}","${String(e?.error ?? '').split('"').join('""')}"`)].join('\n');
                      setErrorCsvHref(`data:text/csv;charset=utf-8,${encodeURIComponent(errorCsv)}`);
                    }
                    await onUploaded({ inserted: response.inserted, updated: response.updated, failed: response.failed });
                    setStep(3);
                  } catch (err) {
                    setResult(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
                  } finally {
                    setUploading(false);
                  }
                }}
              >
                {uploading ? 'Uploading…' : 'Upload CSV'}
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <p><strong>Batch queued for processing.</strong></p>
              {result && <p>{result}</p>}
              {errorCsvHref && <a className="secondary-button severity-secondary" href={errorCsvHref} download="supplier_upload_errors.csv">Download errors CSV</a>}
            </>
          )}
        </div>
        <div className="modal-footer">
          {step > 1 && step < 3 && <button className="secondary-button" onClick={() => setStep((step - 1) as 1 | 2 | 3)}>Back</button>}
          {step === 1 && <button className="primary-button" onClick={() => setStep(2)}>Next — choose file</button>}
          <button className="primary-button" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
